<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >
    <div class="m-2">

      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="4"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />

          <Button
            v-if="getUserPermission('CREATE_LOAN_ACCOUNT_NIB')"
            :label="$t('Add')"
            size="md"
            @click="goToAddLoanAccount"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="8"
        >
          <div class="d-flex flex-wrap align-items-center justify-content-lg-end">
            <!-- Status -->
            <b-col md="3">
              <SelectField
                id="status"
                v-model="accountStatus"
                :label="$t('')"
                :placeholder="$t('Select')"
                :options="translatedLoanAccountStatusFilterOptions"
                :set-selected="fetchLoanAccounts"
                item="label"
                :reduce="option => option.value"
              />
            </b-col>
            <!-- Status -->
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="loanAccountRef"
      :busy="isBusy"
      :items="loanAccountList"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(loanAmount)="data">
        {{ toBalance(data.value) }}
      </template>

      <template #cell(ledgerBalance)="data">
        {{ toBalance(data.value) }}
      </template>

      <template #cell(outstandingLoanPrincipal)="data">
        {{ toBalance(data.value) }}
      </template>

      <template #cell(unpaidPrincipal)="data">
        {{ toBalance(data.value) }}
      </template>

      <template #cell(outstandingLoanFee)="data">
        {{ toBalance(data.value) }}
      </template>

      <template #cell(outstandingLoanInterest)="data">
        {{ toBalance(data.value) }}
      </template>

      <template #cell(dateCreated)="data">
        {{ toDateWithYear(data.value) }}
      </template>

      <template #cell(disbursedLoan)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

      <template #cell(accountStatus)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item
              v-if="getUserPermission('MODIFY_LOAN_ACCOUNT_NIB')"
              @click="goToEditLoanAccount(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">
                {{ $t('Modify') }}
              </span>
            </b-dropdown-item>
            <b-dropdown-item
              @click="onViewRepaymentSchedule(data.item, $event.target)"
            >
              <feather-icon icon="FileTextIcon" />
              <span class="align-middle ml-50">
                {{ $t('Repayment Schedule') }}
              </span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="data.item.accountStatus.toLowerCase() === 'active' && getUserPermission('REPAY_LOAN')"
              @click="onRepayLoan(data.item, $event.target)"
            >
              <feather-icon icon="FileTextIcon" />
              <span class="align-middle ml-50">
                {{ $t('Repay Loan') }}
              </span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="data.item.disbursedLoan === 'Yes' && getUserPermission('RESTRUCTURE_LOAN')"
              @click="onRestructureLoan(data.item, $event.target)"
            >
              <feather-icon icon="RepeatIcon" />
              <span class="align-middle ml-50">
                {{ $t('Restructure Loan') }}
              </span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="data.item.disbursedLoan === 'Yes' && getUserPermission('REFINANCE_LOAN')"
              @click="onRefinanceLoan(data.item, $event.target)"
            >
              <feather-icon icon="RefreshCcwIcon" />
              <span class="align-middle ml-50">
                {{ $t('Refinance Loan') }}
              </span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="getUserPermission('RELEASE_SECURITY_DEPOSIT')"
              @click="onReleaseSecurityDeposit(data.item, $event.target)"
            >
              <feather-icon icon="UnlockIcon" />
              <span class="align-middle ml-50">
                {{ $t('Release Security Deposit') }}
              </span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="!data.item.hasDisbursed && data.item.accountStatus !== 'Revoked'"
              @click="onDisburse(data.item, $event.target)"
            >
              <feather-icon icon="CreditCardIcon" />
              <span class="align-middle ml-50">
                {{ $t('Disburse Loan') }}
              </span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="getUserPermission('CREATE_LOAN_ACCOUNT_NIB') && (!data.item.hasDisbursed && data.item.accountStatus !== 'Revoked')"
              @click="handleRevokeLoan(data.item, $event.target)"
            >
              <feather-icon icon="CreditCardIcon" />
              <span class="align-middle ml-50">
                {{ $t('Revoke Loan') }}
              </span>
            </b-dropdown-item>
            <b-dropdown-item @click="onLoanAgreementLetter(data.item, $event.target)">
              <feather-icon icon="BookOpenIcon" />
              <span class="align-middle ml-50">
                {{ $t('Loan Agreement Letter') }}
              </span>
            </b-dropdown-item>
            <b-dropdown-item @click="onLoanAgreementLetterV2(data.item, $event.target)">
              <feather-icon icon="BookOpenIcon" />
              <span class="align-middle ml-50">
                {{ $t('Loan Agreement Letter V2') }}
              </span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="data.item.collectionMandateReference"
              @click="onViewRemitaMandateHistory(data.item, $event.target)"
            >
              <feather-icon icon="RewindIcon" />
              <span class="align-middle ml-50">
                {{ $t('Remita Mandate History') }}
              </span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="data.item.collectionProviderCustomerID && !data.item.collectionMandateReference"
              @click="onRemitaLoanDisbursement(data.item, $event.target)"
            >
              <feather-icon icon="RefreshCwIcon" />
              <span class="align-middle ml-50">
                {{ $t('Retry Remita Loan Disbursement') }}
              </span>
            </b-dropdown-item>

            <b-dropdown-item @click="goToEditLoanAccount(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">
                {{ $t('View') }}
              </span>
            </b-dropdown-item>

            <!-- <b-dropdown-item
              v-if="getUserPermission(data.item.status === 'Active' ? 'DEACTIVATE_OVERDRAFT' : 'ACTIVATE_OVERDRAFT')"
              @click="loanAccountStatus(data.item, $event.target)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{ getStatus(data.item.status ) }}</span>
            </b-dropdown-item> -->
          </b-dropdown>
        </div>
      </template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchLoanAccounts('', 'main-filter')"
      @cancel="resetFilters"
    >

      <b-row class="match-height">
        <!--Account Name -->
        <b-col md="6">
          <InputField
            id="name"
            v-model="filter.accountName"
            :label="$t('Account Name')"
            :placeholder="$t('Enter account name')"
            type="text"
            onpaste="return false;"
            :is-allow-special-characters="false"
          />
        </b-col>
        <!-- Account Name -->

        <!-- Customer ID -->
        <b-col md="6">
          <InputField
            id="customer-id"
            v-model="filter.customerID"
            :label="$t('Customer ID')"
            :placeholder="$t('Enter Customer ID')"
            type="text"
          />
        </b-col>
        <!-- Customer ID -->

        <!-- Reference Loan Account No -->
        <b-col md="6">
          <InputField
            id="reference-number"
            v-model="filter.referenceNumber"
            :label="$t('Reference No')"
            :placeholder="$t('Enter Reference No')"
            type="text"
          />
        </b-col>
        <!-- Reference Loan Account No -->

        <!-- Account Officer  -->
        <b-col md="6">
          <SelectField
            id="account-officer"
            v-model="filter.accountOfficerCode"
            :label="$t('Account Officer')"
            :reduce="option => option.value"
            :options="accountOfficerOptions"
            item="label"
            :placeholder="$t('Select account officer')"
          />
        </b-col>
        <!--Account Officer  -->

        <!-- Loan account Number -->
        <b-col md="6">
          <InputField
            id="loan-account-no"
            v-model="filter.accountNumber"
            :label="$t('Loan Account No')"
            :placeholder="$t('Enter loan account no')"
            type="number"
          />
        </b-col>
        <!-- Loan account Number -->

        <!-- Linked account Number -->
        <b-col md="6">
          <InputField
            id="linked-account-number"
            v-model="filter.linkedNumber"
            :label="$t('Linked Account Number')"
            :placeholder="$t('Enter linked account no')"
            type="number"
          />
        </b-col>
        <!-- Linked account Number -->

        <!-- IPPIS/Oracle ID -->
        <b-col md="6">
          <InputField
            id="ippis-oracle-id"
            v-model="filter.employeeID"
            :label="$t('IPPIS/Oracle ID')"
            :placeholder="$t('Enter IPPIS/Oracle ID')"
            type="text"
            for="ippis-oracle-id"
          />
        </b-col>
        <!--IPPIS/Oracle ID -->

        <!-- Loan Product -->
        <b-col md="6">
          <SelectField
            id="product"
            v-model="filter.productCode"
            :label="$t('Loan Product')"
            :reduce="option => option.value"
            :options="productOptions"
            item="label"
            :placeholder="$t('Select product')"
            :readonly="isReadonly"
          />
        </b-col>
        <!-- Loan Product -->

        <!-- Has Disbursed Loan-->
        <b-col md="6">
          <SelectField
            id="hasDisbursedLoan"
            v-model="filter.hasDisbursed"
            :label="$t('Loan Disbursed ?')"
            :reduce="option => option.value"
            :options="translatedDisbursedLoanOptions"
            item="label"
            :placeholder="$t('Select')"
          />
        </b-col>
        <!-- Has Disbursed Loan-->

        <!-- Branch -->
        <b-col md="6">
          <SelectField
            id="branch"
            v-model="filter.branchCode"
            :reduce="option => option.value"
            :label="$t('Branch')"
            :placeholder="$t('Select')"
            :options="branchOptions"
            item="label"
          />
        </b-col>
        <!-- Branch -->

        <!-- Status -->
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.accountStatus"
            :label="$t('Status')"
            :placeholder="$t('Select')"
            :options="translatedLoanAccountStatusFilterOptions"
            item="label"
            :reduce="option => option.value"
          />
        </b-col>
        <!-- Status -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->

    <b-modal
      id="edit-repayment-schedule-modal"
      ref="editRepaymentScheduleModal"
      :ok-title="$t('Save Changes')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Edit Repayment Schedule')"
      no-close-on-backdrop
      :ok-disabled="isRepaymentEditOkay"
      @ok="updateRepaymentSchedule"
    >
      <validation-observer ref="editRepaymentScheduleRules">
        <b-form>
          <div class="p-0">
            <b-row>
              <b-col
                md="12"
                class="d-flex align-items-center justify-content-end mb-2 mt-1"
              >
                <Button
                  :label="$t('Add')"
                  size="md"
                  icon="PlusIcon"
                  @click="addNewRecord"
                />
              </b-col>
            </b-row>
            <b-row
              v-for="(item, index) in repaymentSchedulesEdit"
              :key="index"
              class="match-height"
            >
              <b-col md="2">
                <DatePickerField
                  id="repayment-date"
                  v-model="item.paymentDate"
                  :label="$t('Repayment Date')"
                  :config="datePickerConfig"
                  :placeholder="$t('Select Repayment Date')"
                  :error-name="$t('Repayment Date')"
                  rules="required"
                />
              </b-col>
              <b-col md="2">
                <InputNumberField
                  id="principal"
                  v-model="item.principal"
                  :label="$t('Principal')"
                  type="number"
                  :error-name="$t('Principal')"
                  rules="required"
                  @input="calulatePrincipal(index)"
                />
              </b-col>
              <b-col md="2">
                <InputNumberField
                  id="interest"
                  v-model="item.interest"
                  :label="$t('Profit')"
                  type="number"
                  @input="calulateInterest(index)"
                />
              </b-col>
              <b-col md="2">
                <InputNumberField
                  id="loan-fee"
                  v-model="item.fee"
                  :label="$t('Loan Fee')"
                  type="number"
                  @input="calulateFee(index)"
                />
              </b-col>
              <b-col md="3">
                <InputNumberField
                  id="total-repayment"
                  v-model="item.totalRepayment"
                  :label="$t('Total Repayment')"
                  type="number"
                  readonly
                />
              </b-col>
              <b-col
                md="1"
                class="mt-2"
              >
                <feather-icon
                  v-b-tooltip.hover.bottom="'Remove'"
                  icon="XIcon"
                  class="cursor-pointer text-danger"
                  @click="removeRepaymentSchedule(index)"
                />
              </b-col>
            </b-row>
            <b-row>
              <b-col md="2" />
              <b-col
                md="2"
              >
                <span class="font-small-1 section-label">{{ $t('Total Principal') }}:</span> <br>
                <span class="font-weight-bolder font-small-4">{{ toAmount(repaymentTotal.principal) }}</span>
              </b-col>
              <b-col md="2">
                <span class="font-small-1 section-label">{{ $t('Total Profit') }}:</span> <br>
                <span class="font-weight-bolder font-small-4">{{ toAmount(repaymentTotal.interest) }}</span>
              </b-col>
              <b-col md="2">
                <span class="font-small-1 section-label">{{ $t('Total Fee') }}:</span> <br>
                <span class="font-weight-bolder font-small-4">{{ toAmount(repaymentTotal.fee) }}</span>
              </b-col>
              <b-col md="3">
                <Button
                  :label="$t('Check Sum')"
                  size="md"
                  @click="checkSumDifference"
                />
              </b-col>
              <b-col md="1" />
            </b-row>
          </div>
        </b-form>
        <FullScreenSpinner :show="showSpinner" />
      </validation-observer>
    </b-modal>

    <b-modal
      id="repayment-schedule-modal"
      ref="repaymentScheduleModal"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Repayment Schedule')"
    >

      <div class="m-2">

        <!-- Table Top -->
        <b-row>

          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >

            <TableEntries
              v-model="repaymentSchedulePagination.perPage"
              :options="perPageOptions"
            />

          </b-col>

          <!-- Export -->
          <b-col
            cols="12"
            md="6"
          >
            <div class="d-flex align-items-center justify-content-end">
              <Button
                v-if="setAccountStatus === 'Active' && getUserPermission('MODIFY_PAYMENT_SCHEDULE')"
                :label="$t('Edit Repayment Schedule')"
                size="md mr-1"
                @click="editRepaymentSchedule"
              />
              <b-dropdown
                id="dropdown-1"
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                :text="$t('Export')"
                variant="primary"
              >
                <b-dropdown-item @click="excelExport(exportableData)">
                  {{ $t('Excel') }}
                </b-dropdown-item>
                <!-- <b-dropdown-item @click="genPDF()">
                  PDF
                </b-dropdown-item> -->
                <b-dropdown-item @click="pdfExport(exportableData)">
                  {{ $t('PDF') }}
                </b-dropdown-item>
                <b-dropdown-item @click="printRepayment">
                  {{ $t('PRINT') }}
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </b-col>
        </b-row>
      </div>
      <div
        ref="repaymentToPrint"
      >
        <p id="toPrinter">
          <b-row
            style="width: 100%; display: flex"
          >
            <b-col
              style="width: 50%; padding-left: 10px;"
            >
              <img
                style="width: 50% !important"
                :src="userDetails.institutionLogoURL ? userDetails.institutionLogoURL : reportInfo.orgLogo"
              >
              <p
                style="font-size: 16px; font-weight: 700"
                class="mt-1"
              >
                {{ corporateInformation.registeredName }}
              </p>
              <p
                style="font-size: 14px;"
                class="mt-0"
              >
                {{ corporateInformation.registeredAddress }}
              </p>
              <p
                style="font-size: 16px; font-weight: 700;"
                class="mt-3"
              >
                {{ loanAccountHolder }}
              </p>
              <p style="font-size: 14px; ">
                {{ customerAddress }}
              </p>
              <p style="font-size: 14px; ">
                {{ customerPhoneNumber }}
              </p>
            </b-col>
            <b-col
              style="width: 50%"
            >
              <b-card-body>
                <b-card-text>
                  <table
                    align="right"
                  >
                    <tbody>

                      <p style="font-size: 16px; font-weight: 700;">
                        {{ $t('Loan Repayment Information') }}
                      </p>

                      <tr>
                        <td>
                          <p
                            style="font-size: 14px;"
                            class="print-color"
                          >
                            {{ $t('Loan Tenure') }}:
                          </p>
                        </td>
                        <td>
                          <p>
                            {{ `${loanTenure} ${$t('Days')}` }}
                          </p></td>
                      </tr>
                      <tr>
                        <td>
                          <p style="font-size: 14px; color: #000; margin-top: -10px;">
                            {{ $t('Profit Rate PA') }}:
                          </p></td>
                        <td><p style="font-size: 14px; font-weight: 600;">
                          {{ `${interestRate}%` }}
                        </p></td>
                      </tr>
                      <tr>
                        <td><p style="font-size: 14px; color: #000; margin-top: -10px;">
                          {{ $t('Loan Repayment Account') }}:
                        </p></td>
                        <td><p style="font-size: 14px; font-weight: 600;">
                          {{ loanRepaymentAccount }}
                        </p></td>
                      </tr>
                      <tr>
                        <td><p style="font-size: 14px; color: #000; margin-top: -10px;">
                          {{ $t('Loan Account Number') }}:
                        </p></td>
                        <td><p style="font-size: 14px; font-weight: 600;">
                          {{ repaymentLoanAccountNumber }}
                        </p></td>
                      </tr>
                      <tr>
                        <td><p style="font-size: 14px; color: #000; margin-top: -10px;">
                          {{ $t('Product Name') }}:
                        </p></td>
                        <td><p style="font-size: 14px; font-weight: 600;">
                          {{ productName }}
                        </p></td>
                      </tr>
                      <tr>
                        <td><p style="font-size: 14px; color: #000; margin-top: -10px;">
                          {{ $t('Loan Amount') }}:
                        </p></td>
                        <td><p style="font-size: 14px; font-weight: 600;">
                          {{ toBalance(totalLoanAmount) }}
                        </p></td>
                      </tr>
                      <tr>
                        <td><p style="font-size: 14px; color: #000; margin-top: -10px;">
                          {{ $t('Loan Profit') }}:
                        </p></td>
                        <td><p style="font-size: 14px; font-weight: 600;">
                          {{ toBalance(totalLoanInterest) }}
                        </p></td>
                      </tr>
                      <tr>
                        <td><p style="font-size: 14px; color: #000; margin-top: -10px;">
                          {{ $t('Loan Fees') }}:
                        </p></td>
                        <td><p style="font-size: 14px; font-weight: 600;">
                          {{ toBalance(totalLoanFees) }}
                        </p></td>
                      </tr>
                      <tr>
                        <td><p style="font-size: 14px; color: #000; margin-top: -10px;">
                          {{ $t('Expected Full Repayment') }}:
                        </p></td>
                        <td><p style="font-size: 14px; font-weight: 600;">
                          {{ toBalance(totalExpectedRepayment) }}
                        </p></td>
                      </tr>
                      <tr>
                        <td style="border:none;">
                          <br>
                        </td>
                        <td style="border:none;">
                          <br>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </b-card-text>
              </b-card-body>
            </b-col>
          </b-row></p>

        <b-table
          ref="repaymentScheduleModalTable"
          select-mode="single"
          :items="repaymentSchedules"
          responsive
          no-local-sorting
          :fields="translatedRepaymentScheduleTableHeaders"
          primary-key="id"
          show-empty
          :empty-text="$t('No result found')"
          class="position-relative mt-2"
        >
          <template #table-busy>
            <div class="text-center text-danger my-2">
              <b-spinner
                class="align-middle"
                variant="primary"
              />
            </div>
          </template>
          <template #cell(SN)="data">
            {{ repaymentTableIndex(data.index) }}
          </template>

          <template #cell(paymentDate)="data">
            <p :class="getRepaymentColor(data.item.paymentStatus)">
              {{ data.value }}
            </p>
          </template>

          <template #cell(principal)="data">
            <p :class="getRepaymentColor(data.item.paymentStatus)">
              {{ data.value }}
            </p>
          </template>

          <template #cell(interest)="data">
            <p :class="getRepaymentColor(data.item.paymentStatus)">
              {{ data.value }}
            </p>
          </template>

          <template #cell(fee)="data">
            <p :class="getRepaymentColor(data.item.paymentStatus)">
              {{ data.value }}
            </p>
          </template>

          <template #cell(totalRepayment)="data">
            <p :class="getRepaymentColor(data.item.paymentStatus)">
              {{ data.value }}
            </p>
          </template>

          <template #cell(paymentStatus)="data">
            <p :class="getRepaymentColor(data.item.paymentStatus)">
              {{ addSpaceToString(data.value ) }}
            </p>
          </template>
        </b-table>
      </div>
      <!-- <TablePagination
        v-model="repaymentSchedulePagination.currentPage"
        :from="repaymentFrom"
        :to="repaymentTo"
        :total-rows="repaymentSchedulePagination.totalEntries"
        :per-page="repaymentSchedulePagination.perPage"
      /> -->
      <template
        #modal-footer="{ }"
      />
      <FullScreenSpinner :show="showSpinner" />
    </b-modal>

    <!-- modal -->
    <b-modal
      id="repayment-modal"
      ref="repayment-modal"
      :ok-title="$t('Repay Loan')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Repay Loan')"
      no-close-on-backdrop
      :ok-disabled="isRepayingLoan"
      @ok="handleLoanRepayment"
      @hidden="resetRepayModal"
    >
      <validation-observer ref="repayLoanRules">
        <b-form>
          <b-tabs>
            <!-- Repayment Details tab-->
            <b-tab
              :title="$t('Repayment Details')"
              active
            >
              <b-form>
                <b-row class="match-height">
                  <!-- Payment Option -->
                  <b-col md="4">
                    <SelectField
                      id="payment-option"
                      v-model="accountDetails.paymentOption"
                      :label="$t('Payment Option')"
                      :options="translatedPaymentOptions"
                      :reduce="option => option.value"
                      :error-name="$t('Payment Option')"
                      rules="required"
                      item="label"
                      :placeholder="$t('Select')"
                      :set-selected="setFullRepayment"
                    />
                  </b-col>
                  <!-- Payment Option -->
                  <b-col md="12">
                    <app-collapse
                      class="business-items mb-4"
                      type="margin"
                      accordion
                    >
                      <app-collapse-item
                        :title="$t('Repayment Schedules')"
                        class="card-item"
                        is-visible
                      >

                        <b-table
                          ref="paymentScheduleModalTable"
                          select-mode="single"
                          :items="paymentSchedules"
                          responsive
                          no-local-sorting
                          :fields="translatedPaymentScheduleTableHeaders"
                          primary-key="id"
                          show-empty
                          :empty-text="$t('No result found')"
                          class="position-relative mt-2"
                        >
                          <template #cell(SN)="data">
                            {{ data.index + 1 }}
                          </template>

                          <template #cell(paymentDate)="data">
                            <p :class="getRepaymentColor(data.item.paymentStatus)">
                              {{ data.value }}
                            </p>
                          </template>

                          <template #cell(selectPrincipal)="data">
                            <b-form-group class="mt-1">
                              <b-form-checkbox
                                v-model="principalToProcess"
                                :value="data.item.selectPrincipal"
                                :disabled="isFullPayment || data.item.selectPrincipal=== null"
                                @change="sumPrincipal"
                              />
                            </b-form-group>
                          </template>

                          <template #cell(principal)="data">
                            <p :class="getRepaymentColor(data.item.paymentStatus)">
                              {{ toBalance(data.value) }}
                            </p>
                          </template>

                          <template #cell(selectInterest)="data">
                            <b-form-group class="mt-1">
                              <b-form-checkbox
                                v-model="interestToProcess"
                                :value="data.item.selectInterest"
                                :disabled="isFullPayment || data.item.selectInterest=== null"
                                @change="sumInterests"
                              />
                            </b-form-group>
                          </template>

                          <template #cell(interest)="data">
                            <p :class="getRepaymentColor(data.item.paymentStatus)">
                              {{ toBalance(data.value) }}
                            </p>
                          </template>

                          <template #cell(selectFee)="data">
                            <b-form-group class="mt-1">
                              <b-form-checkbox
                                v-model="feesToProcess"
                                :value="data.item.selectFee"
                                :disabled="isFullPayment || data.item.selectFee=== null"
                                @change="sumFees"
                              />
                            </b-form-group>
                          </template>

                          <template #cell(fee)="data">
                            <p :class="getRepaymentColor(data.item.paymentStatus)">
                              {{ toBalance(data.value) }}
                            </p>
                          </template>

                          <template #cell(totalRepayment)="data">
                            <p :class="getRepaymentColor(data.item.paymentStatus)">
                              {{ toBalance(data.value) }}
                            </p>
                          </template>

                          <template #cell(paymentStatus)="data">
                            <p :class="getRepaymentColor(data.item.paymentStatus)">
                              {{ addSpaceToString(data.value ) }}
                            </p>
                          </template>
                        </b-table>
                      </app-collapse-item>
                    </app-collapse>
                  </b-col>
                  <!-- Principal Not Yet Due -->
                  <b-col md="3">
                    <InputField
                      id="principal-not-yet-due"
                      v-model="accountDetails.principalNotYetDue"
                      :label="$t('Principal Not Yet Due')"
                      :placeholder="$t('Enter Principal Not Yet Due')"
                      type="text"
                      readonly
                    />
                  </b-col>
                  <!-- Principal Not Yet Due -->

                  <!--Interest Not Yet Due -->
                  <b-col md="3">
                    <InputField
                      id="interest-not-yet-due"
                      v-model="accountDetails.interestNotYetDue"
                      :label="$t('Profit Not Yet Due')"
                      :placeholder="$t('Enter Profit Not Yet Due')"
                      type="text"
                      readonly
                    />
                  </b-col>
                  <!--Interest Not Yet Due -->

                  <!--Accrued Interest Not Yet Due -->
                  <b-col md="3">
                    <InputField
                      id="accrued-interest-not-yet-due"
                      v-model="accountDetails.accruedInterestNotYetDue"
                      :label="$t('Accrued Profit Not Yet Due')"
                      type="text"
                      readonly
                    />
                  </b-col>
                  <!--Accrued Interest Not Yet Due -->

                  <!--Fees Not Yet Due -->
                  <b-col md="3">
                    <InputField
                      id="fees-not-yet-due"
                      v-model="accountDetails.feesNotYetDue"
                      :label="$t('Fees Not Yet Due')"
                      type="text"
                      readonly
                    />
                  </b-col>
                  <!--Fees Not Yet Due -->

                  <!--Actual Principal To Be Paid -->
                  <b-col md="3">
                    <InputNumberField
                      id="actual-principal-to-be-paid"
                      v-model="accountDetails.actualPrincipalToBePaid"
                      :label="$t('Actual Principal To Be Paid')"
                      :placeholder="$t('Enter Actual Principal To Be Paid')"
                      type="number"
                      :on-input="setTotalAmountToBeRepaidPrincipal"
                      readonly
                    />

                  </b-col>
                  <!--Actual Principal To Be Paid -->

                  <!--Actual Interest To Be Paid -->
                  <b-col md="3">
                    <InputNumberField
                      id="-actual-interest-to-be-paid"
                      v-model="accountDetails.actualInterestToBePaid"
                      :label="$t('Actual Profit To Be Paid')"
                      :placeholder="$t('Enter Actual Profit To Be Paid')"
                      type="number"
                      :error-name="$t('Actual Profit To Be Paid')"
                      :on-input="setTotalAmountToBeRepaidInterest"
                    />
                  </b-col>
                  <!--Actual Interest To Be Paid -->

                  <!--Actual Fees To Be Paid -->
                  <b-col md="3">
                    <InputNumberField
                      id="fees-not-yet-due"
                      v-model="accountDetails.actualFeesToBePaid"
                      :label="$t('Actual Fees To Be Paid')"
                      :placeholder="$t('Enter Actual Fees To Be Paid')"
                      type="number"
                      :error-name="$t('Actual Fees To Be Paid')"
                      rules="required"
                      :on-input="setTotalAmountToBeRepaidFees"
                      readonly
                    />
                  </b-col>
                  <!--Actual Fees To Be Paid -->

                  <!--Other Charges To Be Paid -->
                  <b-col md="3">
                    <InputNumberField
                      id="other-charges-to-be-paid"
                      v-model="accountDetails.otherChargesToBePaid"
                      :label="$t('Other Charges To Be Paid')"
                      :placeholder="$t('Enter Other Charges To Be Paid')"
                      type="number"
                      :on-input="setTotalAmountToBeRepaidCharges"
                    />
                  </b-col>
                  <!--Other Charges To Be Paid -->

                  <!--Total Amount To Be Repaid -->
                  <b-col md="3">
                    <InputNumberField
                      id="total-amount-to-be-repaid"
                      v-model="accountDetails.totalAmountToBeRepaid"
                      :label="$t('Total Amount To Be Repaid')"
                      type="number"
                      readonly
                    />
                  </b-col>
                <!--Total Amount To Be Repaid -->
                </b-row>
              </b-form>
            </b-tab>
            <!-- Loan Account Details tab -->
            <b-tab
              :title="$t('Loan Account Details')"
            >

              <b-form>
                <b-row class="match-height">
                  <b-col md="4">
                    <InputField
                      id="account-name"
                      v-model="accountDetails.accountName"
                      :label="$t('Account Name')"
                      type="text"
                      readonly
                    />
                  </b-col>
                  <b-col md="4">
                    <InputField
                      id="account-number"
                      v-model="accountDetails.accountNumber"
                      :label="$t('Account Number')"
                      type="text"
                      readonly
                    />
                  </b-col>

                  <!-- Product Name -->
                  <b-col md="4">
                    <InputField
                      id="product-name"
                      v-model="accountDetails.productName"
                      :label="$t('Product Name')"
                      type="text"
                      readonly
                    />
                  </b-col>
                  <!-- Product Name -->

                  <!-- Disbursed Amount -->
                  <b-col md="4">
                    <InputField
                      id="disbursed-amount"
                      v-model="accountDetails.disbursedAmount"
                      :label="$t('Disbursed Amount')"
                      type="text"
                      readonly
                    />
                  </b-col>
                  <!-- Disbursed Amount -->

                  <!-- Tenure -->
                  <b-col md="4">
                    <InputField
                      id="tenure"
                      v-model="accountDetails.tenure"
                      :label="$t('Tenure')"
                      type="text"
                      readonly
                    />
                  </b-col>
                  <!-- Tenure -->

                  <!-- Running Period [Start Date - End Date]-->
                  <b-col md="4">
                    <InputField
                      id="running-period"
                      v-model="accountDetails.runningPeriod"
                      :label="$t('Running Period [Start Date - End Date]')"
                      type="text"
                      readonly
                    />
                  </b-col>
                  <!-- Maiden Name -->
                </b-row>
                <!--  Address -->
              </b-form>

            </b-tab>

            <!-- Loan Servicing Account Details tab -->

            <b-tab :title="$t('Loan Servicing Account')">
              <b-row class="match-height">
                <!-- Account Number -->
                <b-col md="4">
                  <InputField
                    id="account-number"
                    v-model="accountDetails.linkedNumber"
                    :label="$t('Account Number')"
                    type="text"
                    readonly
                  />
                </b-col>
                <!-- Account Number -->

                <!-- Product Name -->
                <b-col md="4">
                  <InputField
                    id="product-name"
                    v-model="accountDetails.linkedAccountAccountProduct"
                    :label="$t('Product Name')"
                    type="text"
                    readonly
                  />
                </b-col>
                <!--  Product Name -->

                <!--Account Status -->
                <b-col md="4">
                  <InputField
                    id="account-status"
                    v-model="accountDetails.linkedAccountAccountStatus"
                    :label="$t('Account Status')"
                    type="text"
                    readonly
                  />
                </b-col>
                <!--Account Status -->

                <!--Account Officer -->
                <b-col md="4">
                  <InputField
                    id="account-officer"
                    v-model="accountDetails.linkedAccountAccountOfficer"
                    :label="$t('Account Officer')"
                    type="text"
                    readonly
                  />
                </b-col>
                <!--Account Officer -->

                <!--Ledger Balance -->
                <b-col md="4">
                  <InputField
                    id="ledger-balance"
                    v-model="accountDetails.linkedAccountLedgerBalance"
                    :label="$t('Ledger Balance')"
                    type="text"
                    readonly
                  />
                </b-col>
                <!--Ledger Balance -->

                <!--Available Balance -->
                <b-col md="4">
                  <InputField
                    id="available-balance"
                    v-model="accountDetails.linkedAccountAvailableBalance"
                    :label="$t('Available Balance')"
                    type="text"
                    readonly
                  />
                </b-col>
                <!--Available Balance -->
              </b-row>
            </b-tab>
          </b-tabs>
        </b-form>
      </validation-observer>
    </b-modal>

    <!-- Restructure modal -->
    <b-modal
      id="resturcture-modal"
      ref="resturcture-modal"
      :ok-title="$t('Restructure Loan')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Restructure Loan')"
      no-close-on-backdrop
      :ok-disabled="isRestructuringLoan"
      @ok="handleLoanRestructure"
      @hidden="resetRestructureModal"
    >
      <validation-observer ref="restructureLoanRules">
        <b-form>
          <b-tabs>
            <!-- Repayment Details tab-->
            <b-tab
              :title="$t('Repayment Information Details')"
              active
            >
              <b-form>
                <b-row class="match-height">
                  <b-col md="4">
                    <SelectField
                      id="restructure-amount"
                      v-model="accountDetails.restructureAmountOption"
                      :label="$t('Restructure Amount')"
                      :reduce="option => option.value"
                      :options="translatedRestructureAmountOptions"
                      :placeholder="$t('Select')"
                      :error-name="$t('Restructure Amount')"
                      rules="required"
                      item="label"
                      :set-selected="getRestructureAmount"
                    />
                  </b-col>
                </b-row>

                <b-row class="match-height">
                  <!-- Principal Repayment Option -->
                  <b-col md="4">
                    <SelectField
                      id="principal-repayment-option"
                      v-model="accountDetails.principalRepaymentType"
                      :label="$t('Principal Repayment Option')"
                      :options="repaymentOptions"
                      :reduce="option => option.value"
                      :error-name="$t('Principal Repayment Option')"
                      rules="required"
                      item="label"
                      :placeholder="$t('Select')"
                    />
                  </b-col>
                  <!-- Principal Repayment Option -->

                  <!-- Principal To Be Repaid Every -->
                  <b-col md="4">
                    <InputNumberField
                      id="principal-to-be-repaid-every"
                      v-model="accountDetails.principalToBeRepaidEvery"
                      :label="$t('Principal To Be Repaid Every')"
                      :placeholder="$t('Enter Principal To Be Repaid Every')"
                      type="number"
                      :readonly="equalMonthlyInstallment ? equalMonthlyInstallment : false"
                    />
                  </b-col>
                  <!-- Principal To Be Repaid Every -->

                  <!-- Principal Repayment Frequency -->
                  <b-col md="4">
                    <SelectField
                      id="principal-repayment-frequency"
                      v-model="accountDetails.principalFrequency"
                      :label="$t('Principal Repayment Frequency')"
                      :options="repaymentFrequencyOptions"
                      :reduce="option => option.value"
                      :error-name="$t('Principal Repayment Frequency')"
                      rules="required"
                      item="label"
                      :placeholder="$t('Select')"
                      :readonly="equalMonthlyInstallment ? equalMonthlyInstallment : false"
                    />
                  </b-col>
                  <!-- Principal Repayment Frequency -->
                </b-row>

                <b-row class="match-height">
                  <!-- Over How Many Installment? -->
                  <b-col md="4">
                    <InputField
                      id="principal-installment"
                      v-model="accountDetails.principalInstallment"
                      :label="$t('Over How Many Installment?')"
                      :placeholder="$t('Enter Over How Many Installment?')"
                      type="number"
                      :error-name="$t('Over How Many Installment?')"
                      rules="required"
                      :on-input="setPrincipalInstallment"
                    />
                  </b-col>
                  <!-- Over How Many Installment? -->
                </b-row>

                <b-row class="match-height">
                  <!-- Interest Repayment Option -->
                  <b-col md="4">
                    <SelectField
                      id="principal-repayment-option"
                      v-model="accountDetails.interestRepaymentType"
                      :label="$t('Profit Repayment Option')"
                      :options="repaymentOptions"
                      :reduce="option => option.value"
                      :error-name="$t('Profit Repayment Option')"
                      rules="required"
                      item="label"
                      :placeholder="$t('Select')"
                      :set-selected="checkInterestRepaymentType"
                      :readonly="equalMonthlyInstallment ? equalMonthlyInstallment : false"
                    />
                  </b-col>
                  <!-- Interest Repayment Option -->

                  <!-- Interest To Be Repaid Every -->
                  <b-col
                    v-if="isProrated"
                    md="4"
                  >
                    <InputNumberField
                      id="interest-to-be-repaid-every"
                      v-model="accountDetails.interestToBeRepaidEvery"
                      :label="$t('Profit To Be Repaid Every')"
                      :placeholder="$t('Enter Profit To Be Repaid Every')"
                      type="number"
                      :readonly="equalMonthlyInstallment ? equalMonthlyInstallment : false"
                    />
                  </b-col>
                  <!-- Interest To Be Repaid Every -->

                  <!-- Interest Repayment Frequency -->
                  <b-col md="4">
                    <SelectField
                      id="principal-repayment-frequency"
                      v-model="accountDetails.interestFrequency"
                      :label="$t('Profit Repayment Frequency')"
                      :options="repaymentFrequencyOptions"
                      :reduce="option => option.value"
                      :error-name="$t('Profit Repayment Frequency')"
                      rules="required"
                      item="label"
                      :placeholder="$t('Select')"
                    />
                  </b-col>
                  <!-- Interest Repayment Frequency -->
                </b-row>

                <b-row class="match-height">
                  <!-- Over How Many Installment? -->
                  <b-col md="4">
                    <InputField
                      id="principal-installment"
                      v-model="accountDetails.interestInstallment"
                      :label="$t('Over How Many Installment?')"
                      :placeholder="$t('Enter Over How Many Installment?')"
                      type="number"
                      :error-name="$t('Over How Many Installment?')"
                      rules="required"
                      :readonly="equalMonthlyInstallment ? equalMonthlyInstallment : false"
                    />
                  </b-col>
                  <!-- Over How Many Installment? -->
                </b-row>

                <b-row class="match-height">
                  <b-col
                    md="4"
                  >
                    <SelectField
                      id="applicable-interest"
                      v-model="accountDetails.applicableInterestCode"
                      :label="$t('Select Applicable Profit')"
                      :reduce="option => option.value"
                      :options="interestApplicationOptions"
                      :placeholder="$t('Select')"
                      :error-name="$t('Select Applicable Profit')"
                      rules="required"
                      item="label"
                      :set-selected="getApplicableInterestRate"
                    />
                  </b-col>

                  <!--Applicable Interest Rate -->
                  <b-col md="4">
                    <InputField
                      id="applicable-interest-rate"
                      v-model="accountDetails.applicableInterestRate"
                      :label="$t('Applicable Profit Rate')"
                      :placeholder="$t('Enter Applicable Profit Rate')"
                      type="number"
                    />
                  </b-col>
                  <!--Applicable Interest Rate -->

                  <b-col md="4">
                    <SelectField
                      id="interest-computation-mode"
                      v-model="accountDetails.interestAccrualMode"
                      :label="$t('Profit Computation Mode')"
                      :reduce="option => option.value"
                      :options="interestAccrualOptions"
                      :placeholder="$t('Select')"
                      :error-name="$t('Profit Computation Mode')"
                      rules="required"
                      item="label"
                      :readonly="equalMonthlyInstallment ? equalMonthlyInstallment : false"
                    />
                  </b-col>

                  <!--Penal Interest Rate -->
                  <b-col md="4">
                    <InputField
                      id="penal-interest-rate"
                      v-model="accountDetails.defaultingInterestRate"
                      :label="$t('Penal Profit Rate')"
                      :placeholder="$t('Enter Penal Profit Rate')"
                      type="number"
                    />
                  </b-col>
                  <!--Penal Interest Rate -->

                  <b-col md="4">
                    <SelectField
                      id="restructure-commence-date"
                      v-model="accountDetails.restructureCommencementDateOption"
                      :label="$t('Restructure Commence Date')"
                      :reduce="option => option.value"
                      :options="translatedRestructureCommenceDateOptions"
                      :placeholder="$t('Select')"
                      :error-name="$t('Restructure Commence Date')"
                      rules="required"
                      item="label"
                      :set-selected="getCommencementDate"
                    />
                  </b-col>

                  <b-col md="4">
                    <InputField
                      v-if="!isPreferedDate "
                      id="restructure-date"
                      v-model="accountDetails.restructureStartDate"
                      :label="$t('Restructure Date')"
                      rules="required"
                      :error-name="$t('Restructure Date')"
                      :placeholder="$t('Enter Restructure Date')"
                      type="text"
                      readonly
                    />
                    <DatePickerField
                      v-else
                      id="restructure-date"
                      v-model="accountDetails.restructureStartDate"
                      rules="required"
                      :label="$t('Restructure Date')"
                      :error-name="$t('Restructure Date')"
                      :config="datePickerConfig"
                      :placeholder="$t('Select Restructure Date')"
                    />
                  </b-col>
                  <b-col md="4">
                    <DatePickerField
                      id="first-repayment-date"
                      v-model="accountDetails.firstRepaymentDate"
                      :label="$t('Loan First Repayment Date')"
                      :config="datePickerConfig"
                      :placeholder="$t('Select Loan First Repayment Date')"
                      :disabled="isReadonly"
                    />
                  </b-col>
                </b-row>
                <b-row v-if="showComputedInterest">
                  <b-col
                    md="6"
                  >
                    <TextAreaField
                      id="computed-interest"
                      v-model="computedInterest"
                      :label="$t('Computed Profit')"
                      type="text"
                      rows="2"
                      readonly
                    />
                  </b-col>
                </b-row>
                <div class="d-flex flex-wrap">
                  <Button
                    :label="$t('Recompute Profit')"
                    :loading="isLoadingComputeInterest"
                    size="sm"
                    class="mt-3 mb-2"
                    @click="computeInterest"
                  />

                  <Button
                    :label="$t('View Original Loan Schedule')"
                    :loading="isLoadingViewOriginalSchedule"
                    size="sm"
                    class="mt-3 mb-2 ml-lg-2 ml-md-2"
                    @click="onViewRepaymentSchedules"
                  />
                  <Button
                    :label="$t('View New Schedule After Restructuring')"
                    :loading="isLoadingNewViewOriginalSchedule"
                    size="sm"
                    class="mt-3 mb-2 ml-lg-2 ml-md-2"
                    @click="onViewNewRepaymentSchedules"
                  />

                  <Button
                    :label="$t('Remita Lookup Search')"
                    size="sm"
                    class="mb-2"
                    @click="onViewRemitaLookup"
                  />
                </div>
              </b-form>
            </b-tab>
            <!-- Loan Account Details tab -->
            <b-tab
              :title="$t('Loan Account Details')"
            >

              <b-form>
                <b-row class="match-height">
                  <b-col md="4">
                    <InputField
                      id="account-name"
                      v-model="accountDetails.accountName"
                      :label="$t('Account Name')"
                      type="text"
                      readonly
                    />
                  </b-col>
                  <b-col md="4">
                    <InputField
                      id="account-number"
                      v-model="accountDetails.accountNumber"
                      :label="$t('Account Number')"
                      type="text"
                      readonly
                    />
                  </b-col>

                  <!-- Product Name -->
                  <b-col md="4">
                    <InputField
                      id="product-name"
                      v-model="accountDetails.productName"
                      :label="$t('Product Name')"
                      type="text"
                      readonly
                    />
                  </b-col>
                  <!-- Product Name -->

                  <!-- Disbursed Amount -->
                  <b-col md="4">
                    <InputField
                      id="disbursed-amount"
                      v-model="accountDetails.disbursedAmount"
                      :label="$t('Disbursed Amount')"
                      type="text"
                      readonly
                    />
                  </b-col>
                  <!-- Disbursed Amount -->

                  <!-- Principal Balance -->
                  <b-col md="4">
                    <InputField
                      id="principal-balance"
                      v-model="accountDetails.principalBalance"
                      :label="$t('Principal Balance')"
                      type="text"
                      readonly
                    />
                  </b-col>
                  <!-- Principal Balance -->

                  <!-- Principal Due & Unpaid -->
                  <b-col md="4">
                    <InputField
                      id="principal-balance"
                      v-model="accountDetails.principalDueAndUnpaid"
                      :label="$t('Principal Due & Unpaid')"
                      type="text"
                      readonly
                    />
                  </b-col>
                  <!-- Principal Due & Unpaid -->

                  <!-- Interest Due & Unpaid -->
                  <b-col md="4">
                    <InputField
                      id="principal-balance"
                      v-model="accountDetails.interestDueAndUnpaid"
                      :label="$t('Profit Due & Unpaid')"
                      type="text"
                      readonly
                    />
                  </b-col>
                  <!-- Interest Due & Unpaid -->

                  <!-- Tenure -->
                  <b-col md="4">
                    <InputField
                      id="tenure"
                      v-model="accountDetails.tenure"
                      :label="$t('Tenure')"
                      type="text"
                      readonly
                    />
                  </b-col>
                  <!-- Tenure -->

                  <!-- Running Period [Start Date - End Date]-->
                  <b-col md="4">
                    <InputField
                      id="running-period"
                      v-model="accountDetails.runningPeriod"
                      :label="$t('Running Period [Start Date - End Date]')"
                      type="text"
                      readonly
                    />
                  </b-col>
                  <!-- Maiden Name -->
                </b-row>
                <!--  Address -->
              </b-form>

            </b-tab>

            <!-- Loan Servicing Account Details tab -->

            <b-tab :title="$t('Loan Servicing Account')">
              <b-row class="match-height">
                <!-- Account Number -->
                <b-col md="4">
                  <InputField
                    id="account-number"
                    v-model="accountDetails.linkedNumber"
                    :label="$t('Account Number')"
                    type="text"
                    readonly
                  />
                </b-col>
                <!-- Account Number -->

                <!-- Product Name -->
                <b-col md="4">
                  <InputField
                    id="product-name"
                    v-model="accountDetails.linkedAccountAccountProduct"
                    :label="$t('Product Name')"
                    type="text"
                    readonly
                  />
                </b-col>
                <!--  Product Name -->

                <!--Account Status -->
                <b-col md="4">
                  <InputField
                    id="account-status"
                    v-model="accountDetails.linkedAccountAccountStatus"
                    :label="$t('Account Status')"
                    type="text"
                    readonly
                  />
                </b-col>
                <!--Account Status -->

                <!--Account Officer -->
                <b-col md="4">
                  <InputField
                    id="account-officer"
                    v-model="accountDetails.linkedAccountAccountOfficer"
                    :label="$t('Account Officer')"
                    type="text"
                    readonly
                  />
                </b-col>
                <!--Account Officer -->

                <!--Ledger Balance -->
                <b-col md="4">
                  <InputField
                    id="ledger-balance"
                    v-model="accountDetails.linkedAccountLedgerBalance"
                    :label="$t('Ledger Balance')"
                    type="text"
                    readonly
                  />
                </b-col>
                <!--Ledger Balance -->

                <!--Available Balance -->
                <b-col md="4">
                  <InputField
                    id="available-balance"
                    v-model="accountDetails.linkedAccountAvailableBalance"
                    :label="$t('Available Balance')"
                    type="text"
                    readonly
                  />
                </b-col>
                <!--Available Balance -->
              </b-row>
            </b-tab>
          </b-tabs>
        </b-form>
      </validation-observer>
    </b-modal>
    <!-- Restructure modal -->

    <!-- Refinance modal -->
    <b-modal
      id="refinance-modal"
      ref="refinance-modal"
      :ok-title="$t('Refinance Loan')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Refinance Loan')"
      no-close-on-backdrop
      :ok-disabled="isRefinancingLoan"
      @ok="handleLoanRefinance"
      @hidden="resetRefinanceModal"
    >
      <validation-observer ref="refinanceLoanRules">
        <b-form>
          <b-tabs>
            <!-- Repayment Details tab-->
            <b-tab
              :title="$t('Repayment Information Details')"
              active
            >
              <!-- <b-form> -->
              <b-row class="match-height">
                <!-- Refinance By How Much? -->
                <b-col md="4">
                  <InputNumberField
                    id="refinance-by-how-much"
                    v-model="accountDetails.refinanceAmount"
                    :error-name="$t('Refinance By How Much')"
                    rules="required"
                    :label="$t('Refinance By How Much?')"
                    :placeholder="$t('Refinance By How Much?')"
                  />
                </b-col>
                <!-- Refinance By How Much? -->
              </b-row>
              <b-row class="match-height">
                <!-- Principal Repayment Option -->
                <b-col md="4">
                  <SelectField
                    id="principal-repayment-option"
                    v-model="accountDetails.principalRepaymentType"
                    :label="$t('Principal Repayment Option')"
                    :options="repaymentOptions"
                    :reduce="option => option.value"
                    :error-name="$t('Principal Repayment Option')"
                    rules="required"
                    item="label"
                    :placeholder="$t('Select')"
                  />
                </b-col>
                <!-- Principal Repayment Option -->

                <!-- Principal To Be Repaid Every -->
                <b-col md="4">
                  <InputField
                    id="principal-to-be-repaid-every"
                    v-model="accountDetails.principalToBeRepaidEvery"
                    type="number"
                    :label="$t('Principal To Be Repaid Every')"
                    :error-name="$t('Principal To Be Repaid Every')"
                    rules="required"
                    :placeholder="$t('Enter Principal To Be Repaid Every')"
                    :readonly="equalMonthlyInstallment ? equalMonthlyInstallment : false"
                  />
                </b-col>
                <!-- Principal To Be Repaid Every -->

                <!-- Principal Repayment Frequency -->
                <b-col md="4">
                  <SelectField
                    id="principal-repayment-frequency"
                    v-model="accountDetails.principalFrequency"
                    :label="$t('Principal Repayment Frequency')"
                    :options="repaymentFrequencyOptions"
                    :reduce="option => option.value"
                    :error-name="$t('Principal Repayment Frequency')"
                    rules="required"
                    item="label"
                    :placeholder="$t('Select')"
                    :readonly="equalMonthlyInstallment ? equalMonthlyInstallment : false"
                  />
                </b-col>
                <!-- Principal Repayment Frequency -->
              </b-row>
              <b-row class="match-height">
                <!-- Over How Many Installment? -->
                <b-col md="4">
                  <InputField
                    id="principal-installment"
                    v-model="accountDetails.principalInstallment"
                    :label="$t('Over How Many Installment?')"
                    :placeholder="$t('Enter Over How Many Installment?')"
                    type="number"
                    :error-name="$t('Over How Many Installment?')"
                    rules="required"
                    :on-input="setPrincipalInstallment"
                  />
                </b-col>
                <!-- Over How Many Installment? -->
              </b-row>
              <b-row class="match-height">
                <!-- Interest Repayment Option -->
                <b-col md="4">
                  <SelectField
                    id="principal-repayment-option"
                    v-model="accountDetails.interestRepaymentType"
                    :label="$t('Profit Repayment Option')"
                    :options="repaymentOptions"
                    :reduce="option => option.value"
                    :error-name="$t('Profit Repayment Option')"
                    rules="required"
                    item="label"
                    :placeholder="$t('Select')"
                    :set-selected="checkInterestRepaymentType"
                    :readonly="equalMonthlyInstallment ? equalMonthlyInstallment : false"
                  />
                </b-col>
                <!-- Interest Repayment Option -->

                <!-- Interest To Be Repaid Every -->
                <b-col
                  v-if="isProrated"
                  md="4"
                >
                  <InputField
                    id="interest-to-be-repaid-every"
                    v-model="accountDetails.interestToBeRepaidEvery"
                    :label="$t('Profit To Be Repaid Every')"
                    type="number"
                    :error-name="$t('Profit To Be Repaid Every')"
                    rules="required"
                    :placeholder="$t('Enter Profit To Be Repaid Every')"
                    :readonly="equalMonthlyInstallment ? equalMonthlyInstallment : false"
                  />
                </b-col>
                <!-- Interest To Be Repaid Every -->

                <!-- Interest Repayment Frequency -->
                <b-col md="4">
                  <SelectField
                    id="principal-repayment-frequency"
                    v-model="accountDetails.interestFrequency"
                    :label="$t('Profit Repayment Frequency')"
                    :options="repaymentFrequencyOptions"
                    :reduce="option => option.value"
                    :error-name="$t('Profit Repayment Frequency')"
                    rules="required"
                    item="label"
                    :placeholder="$t('Select')"
                    :readonly="equalMonthlyInstallment ? equalMonthlyInstallment : false"
                  />
                </b-col>
                <!-- Interest Repayment Frequency -->
              </b-row>

              <b-row class="match-height">
                <!-- Over How Many Installment? -->
                <b-col md="4">
                  <InputField
                    id="principal-installment"
                    v-model="accountDetails.interestInstallment"
                    :label="$t('Over How Many Installment?')"
                    :placeholder="$t('Enter Over How Many Installment?')"
                    type="number"
                    :error-name="$t('Over How Many Installment?')"
                    rules="required"
                    :readonly="equalMonthlyInstallment ? equalMonthlyInstallment : false"
                  />
                </b-col>
                <!-- Over How Many Installment? -->
              </b-row>

              <b-row class="match-height">
                <b-col
                  md="4"
                >
                  <SelectField
                    id="applicable-interest"
                    v-model="accountDetails.applicableInterestCode"
                    :label="$t('Select Applicable Profit')"
                    :reduce="option => option.value"
                    :options="interestApplicationOptions"
                    :placeholder="$t('Select')"
                    :error-name="$t('Select Applicable Profit')"
                    rules="required"
                    item="label"
                    :set-selected="getApplicableInterestRate"
                  />
                </b-col>

                <!--Applicable Interest Rate -->
                <b-col md="4">
                  <InputField
                    id="applicable-interest-rate"
                    v-model="accountDetails.applicableInterestRate"
                    :label="$t('Applicable Profit Rate')"
                    :placeholder="$t('Enter Applicable Profit Rate')"
                    type="number"
                  />
                </b-col>
                <!--Applicable Interest Rate -->

                <b-col md="4">
                  <SelectField
                    id="interest-computation-mode"
                    v-model="accountDetails.interestAccrualMode"
                    :label="$t('Profit Computation Mode')"
                    :reduce="option => option.value"
                    :options="interestAccrualOptions"
                    :placeholder="$t('Select')"
                    :error-name="$t('Profit Computation Mode')"
                    rules="required"
                    item="label"
                    :readonly="equalMonthlyInstallment ? equalMonthlyInstallment : false"
                  />
                </b-col>

                <!--Defaulting Loan Interest Rate -->
                <b-col md="4">
                  <InputField
                    id="penal-interest-rate"
                    v-model="accountDetails.defaultingInterestRate"
                    :label="$t('Defaulting Loan Profit Rate')"
                    :placeholder="$t('Defaulting Loan Profit Rate')"
                    type="number"
                  />
                </b-col>
                <!--Defaulting Loan Interest Rate -->

                <b-col md="4">
                  <SelectField
                    id="refinance-commence-date"
                    v-model="accountDetails.refinanceCommencementDateOption"
                    :label="$t('Refinance Commence Date')"
                    :reduce="option => option.value"
                    :options="translatedRefinanceCommenceDateOptions"
                    :placeholder="$t('Select')"
                    :error-name="$t('Refinance Commence Date')"
                    rules="required"
                    item="label"
                    :set-selected="getRefinanceCommencementDate"
                  />
                </b-col>

                <b-col md="4">
                  <InputField
                    v-if="!isPreferedDate "
                    id="refinance-date"
                    v-model="accountDetails.refinanceStartDate"
                    :label="$t('Refinance Date')"
                    rules="required"
                    :error-name="$t('Refinance Date')"
                    :placeholder="$t('Enter Refinance Date')"
                    type="text"
                    readonly
                  />
                  <DatePickerField
                    v-else
                    id="refinance-date"
                    v-model="accountDetails.refinanceStartDate"
                    rules="required"
                    :label="$t('Refinance Date')"
                    :error-name="$t('Refinance Date')"
                    :config="datePickerConfig"
                    :placeholder="$t('Select Refinance Date')"
                  />
                </b-col>
                <b-col md="4">
                  <DatePickerField
                    id="first-repayment-date"
                    v-model="accountDetails.firstRepaymentDate"
                    :label="$t('Loan First Repayment Date')"
                    :config="datePickerConfig"
                    :placeholder="$t('Select Loan First Repayment Date')"
                    :disabled="isReadonly"
                  />
                </b-col>
              </b-row>
              <b-row class="mt-3">
                <b-col
                  cols="12"
                  class="mb-2"
                >
                  <h5 class="mb-0">
                    {{ $t('Loan Fee Information') }}
                  </h5>
                </b-col>

                <b-col md="4">
                  <SwitchButton
                    v-model="accountDetails.applyLoanFees"
                    :label="$t('Apply Loan Fee')"
                    name="apply-loan-fee"
                    class="mt-2"
                  />
                </b-col>
                <b-col
                  v-if="setApplyLoanFee(accountDetails.applyLoanFees)"
                  md="8"
                >
                  <SelectField
                    id="loan-fee"
                    v-model="accountDetails.loanFees"
                    :label="$t('Loan Fee')"
                    :reduce="option => option.value"
                    :options="loanFeeOptions"
                    :placeholder="$t('Select')"
                    :error-name="$t('Select Loan Fee')"
                    item="label"
                    multiple
                  />
                </b-col>
              </b-row>

              <b-row v-if="showComputedInterest">
                <b-col
                  md="6"
                >
                  <TextAreaField
                    id="computed-interest"
                    v-model="computedInterest"
                    :label="$t('Computed Profit')"
                    type="text"
                    rows="2"
                    readonly
                  />
                </b-col>
              </b-row>
              <b-row class="mt-3 p-1">
                <div class="d-flex flex-wrap">
                  <Button
                    :label="$t('Recompute Profit')"
                    :loading="isLoadingComputeInterestRefinancing"
                    size="sm"
                    class="mt-3 mb-2"
                    @click="refinanceComputeInterest"
                  />

                  <Button
                    :label="$t('View Original Loan Schedule')"
                    :loading="isLoadingViewOriginalSchedule"
                    size="sm"
                    class="mt-3 mb-2 ml-lg-2 ml-md-2"
                    @click="onViewRepaymentSchedules"
                  />
                  <Button
                    :label="$t('View New Schedule After Refinancing')"
                    :loading="isLoadingNewViewOriginalScheduleRefinancing"
                    size="sm"
                    class="mt-3 mb-2 ml-lg-2 ml-md-2"
                    @click="onViewRefinance"
                  />
                  <Button
                    :label="$t('Remita Lookup Search')"
                    size="sm"
                    class="mb-2"
                    @click="onViewRemitaLookup"
                  />
                </div>
              </b-row>
            </b-tab>
            <!-- Loan Account Details tab -->
            <b-tab
              :title="$t('Loan Account Details')"
            >

              <b-row class="match-height">
                <b-col md="4">
                  <InputField
                    id="account-name"
                    v-model="accountDetails.accountName"
                    :label="$t('Account Name')"
                    type="text"
                    readonly
                  />
                </b-col>
                <b-col md="4">
                  <InputField
                    id="account-number"
                    v-model="accountDetails.accountNumber"
                    :label="$t('Account Number')"
                    type="text"
                    readonly
                  />
                </b-col>

                <!-- Product Name -->
                <b-col md="4">
                  <InputField
                    id="product-name"
                    v-model="accountDetails.productName"
                    :label="$t('Product Name')"
                    type="text"
                    readonly
                  />
                </b-col>
                <!-- Product Name -->

                <!-- Disbursed Amount -->
                <b-col md="4">
                  <InputField
                    id="disbursed-amount"
                    v-model="accountDetails.disbursedAmount"
                    :label="$t('Disbursed Amount')"
                    type="text"
                    readonly
                  />
                </b-col>
                <!-- Disbursed Amount -->

                <!-- Principal Balance -->
                <b-col md="4">
                  <InputField
                    id="principal-balance"
                    v-model="accountDetails.principalBalance"
                    :label="$t('Principal Balance')"
                    type="text"
                    readonly
                  />
                </b-col>
                <!-- Principal Balance -->

                <!-- Principal Due & Unpaid -->
                <b-col md="4">
                  <InputField
                    id="principal-balance"
                    v-model="accountDetails.principalDueAndUnpaid"
                    :label="$t('Principal Due & Unpaid')"
                    type="text"
                    readonly
                  />
                </b-col>
                <!-- Principal Due & Unpaid -->

                <!-- Interest Due & Unpaid -->
                <b-col md="4">
                  <InputField
                    id="principal-balance"
                    v-model="accountDetails.interestDueAndUnpaid"
                    :label="$t('Profit Due & Unpaid')"
                    type="text"
                    readonly
                  />
                </b-col>
                <!-- Interest Due & Unpaid -->

                <!-- Tenure -->
                <b-col md="4">
                  <InputField
                    id="tenure"
                    v-model="accountDetails.tenure"
                    :label="$t('Tenure')"
                    type="text"
                    readonly
                  />
                </b-col>
                <!-- Tenure -->

                <!-- Running Period [Start Date - End Date]-->
                <b-col md="4">
                  <InputField
                    id="running-period"
                    v-model="accountDetails.runningPeriod"
                    :label="$t('Running Period [Start Date - End Date]')"
                    type="text"
                    readonly
                  />
                </b-col>
                <!-- Maiden Name -->
              </b-row>
              <!--  Address -->

            </b-tab>

            <!-- Loan Servicing Account Details tab -->

            <b-tab :title="$t('Loan Servicing Account')">
              <b-row class="match-height">
                <!-- Account Number -->
                <b-col md="4">
                  <InputField
                    id="account-number"
                    v-model="accountDetails.linkedNumber"
                    :label="$t('Account Number')"
                    type="text"
                    readonly
                  />
                </b-col>
                <!-- Account Number -->

                <!-- Product Name -->
                <b-col md="4">
                  <InputField
                    id="product-name"
                    v-model="accountDetails.linkedAccountAccountProduct"
                    :label="$t('Product Name')"
                    type="text"
                    readonly
                  />
                </b-col>
                <!--  Product Name -->

                <!--Account Status -->
                <b-col md="4">
                  <InputField
                    id="account-status"
                    v-model="accountDetails.linkedAccountAccountStatus"
                    :label="$t('Account Status')"
                    type="text"
                    readonly
                  />
                </b-col>
                <!--Account Status -->

                <!--Account Officer -->
                <b-col md="4">
                  <InputField
                    id="account-officer"
                    v-model="accountDetails.linkedAccountAccountOfficer"
                    :label="$t('Account Officer')"
                    type="text"
                    readonly
                  />
                </b-col>
                <!--Account Officer -->

                <!--Ledger Balance -->
                <b-col md="4">
                  <InputField
                    id="ledger-balance"
                    v-model="accountDetails.linkedAccountLedgerBalance"
                    :label="$t('Ledger Balance')"
                    type="text"
                    readonly
                  />
                </b-col>
                <!--Ledger Balance -->

                <!--Available Balance -->
                <b-col md="4">
                  <InputField
                    id="available-balance"
                    v-model="accountDetails.linkedAccountAvailableBalance"
                    :label="$t('Available Balance')"
                    type="text"
                    readonly
                  />
                </b-col>
                <!--Available Balance -->
              </b-row>
            </b-tab>
          </b-tabs>
        </b-form>
      </validation-observer>
    </b-modal>

    <b-modal
      id="info-modal"
      ref="infoModal"
      centered
      size="md"
      ok-only
      :ok-title="$t('Okay')"
      @ok="hideModal('info-modal')"
    >
      <div class="p-2">
        <p>{{ $t('External Customer Reference') }}</p>
      </div>
    </b-modal>

    <b-modal
      id="mandate-history-modal"
      ref="mandateHistoryModal"
      :ok-title="$t('Cancel')"
      ok-only
      ok-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('View Mandate History')"
    >
      <div class="p-2">
        <b-row class="match-height">
          <b-col md="4">
            <InputField
              id="customer-id"
              v-model="mandateHistoryList.customerId"
              :label="$t('Customer ID')"
              type="text"
              readonly
            />
          </b-col>
          <b-col md="4">
            <InputField
              id="name"
              v-model="mandateHistoryList.lastName"
              :label="$t('Name')"
              type="text"
              readonly
            />
          </b-col>
          <b-col md="4">
            <InputField
              id="phone-number"
              v-model="mandateHistoryList.phoneNumber"
              :label="$t('Phone Number')"
              type="text"
              readonly
            />
          </b-col>
          <b-col md="4">
            <InputField
              id="mandate-reference"
              v-model="mandateHistoryList.loanMandateReference"
              :label="$t('Mandate Reference')"
              type="text"
              readonly
            />
          </b-col>
          <b-col md="4">
            <InputNumberField
              id="total-disbursed"
              v-model="mandateHistoryList.totalDisbursed"
              :label="$t('Total Disbursed')"
              type="text"
              readonly
            />
          </b-col>
          <b-col md="4">
            <InputNumberField
              id="outstanding-loan-bal"
              v-model="mandateHistoryList.outstandingLoanBal"
              :label="$t('Outstanding Loan Balance')"
              type="text"
              readonly
            />
          </b-col>
          <b-col md="4">
            <InputField
              id="employer-name"
              v-model="mandateHistoryList.employerName"
              :label="$t('Employer Name')"
              type="text"
              readonly
            />
          </b-col>
          <b-col md="4">
            <InputField
              id="authorization-code"
              v-model="mandateHistoryList.authorisationCode"
              :label="$t('Authorization Code')"
              type="text"
              readonly
            />
          </b-col>
          <b-col md="4">
            <InputField
              id="salary-bank"
              v-model="mandateHistoryList.salaryBankCode"
              :label="$t('Salary Bank')"
              type="text"
              readonly
            />
          </b-col>
          <b-col md="4">
            <InputField
              id="salary-account"
              v-model="mandateHistoryList.salaryAccount"
              :label="$t('Salary Account')"
              type="text"
              readonly
            />
          </b-col>
          <b-col md="4">
            <InputField
              id="date-of-disbursement"
              v-model="mandateHistoryList.dateOfDisbursement"
              :label="$t('Date Of Disbursement')"
              type="text"
              readonly
            />
          </b-col>
          <b-col md="4">
            <InputField
              id="collection-start-date"
              v-model="mandateHistoryList.collectionStartDate"
              :label="$t('Collection Start Date')"
              type="text"
              readonly
            />
          </b-col>
          <b-col md="4">
            <InputField
              id="Repayment"
              v-model="mandateHistoryList.repayment"
              :label="$t('Repayment')"
              type="text"
              readonly
            />
          </b-col>
          <b-col md="4">
            <InputField
              id="status"
              v-model="mandateHistoryList.status"
              :label="$t('Status')"
              type="text"
              readonly
            />
          </b-col>
          <b-col md="4">
            <InputField
              id="lender-details"
              v-model="mandateHistoryList.lenderDetails"
              :label="$t('Lender Details')"
              type="text"
              readonly
            />
          </b-col>
        </b-row>
      </div>
    </b-modal>

    <b-modal
      id="remita-loan-disbursement-modal"
      ref="remitaLoanDisbursementModal"
      :ok-title="$t('Cancel')"
      ok-only
      ok-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Remita Loan Disbursement')"
    >
      <div class="p-2">
        <b-row class="match-height">
          <b-col md="6">
            <InputField
              id="response-code"
              v-model="remitaLoanDisbursementList.responseCode"
              :label="$t('Response Code')"
              type="text"
              readonly
            />
          </b-col>
          <b-col md="6">
            <InputField
              id="response-message"
              v-model="remitaLoanDisbursementList.responseMsg"
              :label="$t('Response Message')"
              type="text"
              readonly
            />
          </b-col>
        </b-row>
      </div>
    </b-modal>
    <!-- Remita Lookup Search -->
    <b-modal
      id="remita-lookup-search-table"
      ref="remitaLookupSearchTable"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      no-close-on-backdrop
      size="lg"
      :title="$t('Remita Lookup Search')"
    >

      <div class="m-2">

        <!-- Table Top -->
        <b-row>

          <!-- Per Page -->
          <b-col
            cols="12"
            md="12"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >

            <!-- <TableEntries
              v-model="repaymentSchedulePagination.perPage"
              :options="perPageOptions"
            /> -->
            <validation-observer ref="remitaLookupRef">
              <b-form @submit.prevent="handleRemitaSearch">
                <b-row class="match-height">

                  <b-col md="4">
                    <InputField
                      id="first-name"
                      v-model="remitaLookUpData.firstName"
                      :label="$t('First Name')"
                      :placeholder="$t('First Name')"
                      type="text"
                    />
                  </b-col>
                  <b-col md="4">
                    <InputField
                      id="last-name"
                      v-model="remitaLookUpData.lastName"
                      :label="$t('Last Name')"
                      :placeholder="$t('Last Name')"
                      type="text"
                    />
                  </b-col>
                  <b-col md="4">
                    <InputField
                      id="middle-name"
                      v-model="remitaLookUpData.middleName"
                      :label="$t('Middle Name')"
                      :placeholder="$t('Middle Name')"
                      type="text"
                    />
                  </b-col>
                  <b-col md="4">
                    <SelectField
                      id="bank-code"
                      v-model="remitaLookUpData.bankCode"
                      :label="$t('Salary Bank')"
                      :options="salaryBankOptions"
                      :placeholder="$t('Select')"
                      :reduce="option => option.value"
                      item="label"
                      rules="required"
                      :error-name="$t('Salary Bank')"
                      :readonly="isEditable"
                    />
                  </b-col>
                  <b-col md="4">
                    <InputField
                      id="account-number"
                      v-model="remitaLookUpData.accountNumber"
                      :label="$t('Salary Account Number')"
                      :placeholder="$t('Salary Account Number')"
                      type="number"
                      rules="required"
                      :error-name="$t('Salary Account Number')"
                    />
                  </b-col>
                  <b-col md="4">
                    <InputField
                      id="bvn"
                      v-model="remitaLookUpData.bvn"
                      :label="$t('BVN')"
                      :placeholder="$t('BVN')"
                      type="text"
                    />
                  </b-col>

                  <b-col md="4">
                    <InputField
                      id="authorization-code"
                      v-model="remitaLookUpData.authorisationCode"
                      :label="$t('Authorisation Code')"
                      :placeholder="$t('Authorisation Code')"
                      type="text"
                      :rules="`required|authorisation-min: ${100000}|authorisation-max: ${9999999999}`"
                      :error-name="$t('Authorisation Code')"
                    />
                  </b-col>

                  <b-col md="4">
                    <Button
                      :label="$t('Search')"
                      size="md"
                      class="mt-2"
                      :loading="isRemitaSearchLoading"
                      :disabled="isRemitaSearchDisabled"
                      @click="handleRemitaSearch"
                    />
                  </b-col>
                  <b-col
                    v-if="isProceedingWithRemita"
                    md="4"
                  >
                    <Button
                      :label="$t('Proceed')"
                      size="md"
                      class="mt-2"
                      @click="proceedWithRemita"
                    />
                  </b-col>
                </b-row>
              </b-form>
            </validation-observer>

          </b-col>

        </b-row>

      </div>
      <b-col
        cols="12"
        class="mt-1 mb-0"
      >
        <h5 class="section-label mb-0">
          {{ $t('Salary Payment Details') }}
        </h5>
      </b-col>

      <b-table
        ref="salaryPaymentTable"
        select-mode="single"
        :items="salaryPaymentDetails"
        responsive
        :fields="salaryPaymentTableHeaders"
        :per-page="salaryPaymentDetailsPagination.perPage"
        :current-page="salaryPaymentDetailsPagination.currentPage"
        primary-key="id"
        show-empty
        :empty-text="$t('No result found')"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        class="position-relative mt-2"
      >
        <template #cell(SN)="data">
          {{ salaryPaymentPageNumber(data.index) }}
        </template>

        <template #cell(amount)="data">
          {{ toBalance(data.value) }}
        </template>

        <template #cell(paymentDate)="data">
          {{ data.value ? toDateWithYear(checkDateFormat(data.value)) : '' }}
        </template>
      </b-table>
      <TablePagination
        v-model="salaryPaymentDetailsPagination.currentPage"
        :from="salaryPaymentDetailsFrom"
        :to="salaryPaymentDetailsTo"
        :total-rows="salaryPaymentDetailsPagination.totalEntries"
        :per-page="salaryPaymentDetailsPagination.perPage"
      />

      <b-col
        cols="12"
        class="mt-3 mb-0"
      >
        <h5 class="section-label mb-0">
          {{ $t('Loan History Details') }}
        </h5>
      </b-col>
      <b-table
        ref="loanHistoryTable"
        select-mode="single"
        :items="loanHistoryDetails"
        responsive
        :fields="translatedLoanHistoryTableHeaders"
        :per-page="loanHistoryDetailsPagination.perPage"
        :current-page="loanHistoryDetailsPagination.currentPage"
        primary-key="id"
        show-empty
        :empty-text="$t('No result found')"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        class="position-relative mt-2"
      >
        <template #cell(SN)="data">
          {{ getLoanHistoryPageNumber(data.index) }}
        </template>

        <template #cell(loanAmount)="data">
          {{ toBalance(data.value) }}
        </template>
        <template #cell(outstandingAmount)="data">
          {{ toBalance(data.value) }}
        </template>
        <template #cell(repaymentAmount)="data">
          {{ toBalance(data.value) }}
        </template>

        <template #cell(loanDisbursementDate)="data">
          {{ data.value ? toDateWithYear(checkDateFormat(data.value)) : '' }}
        </template>

        <template #cell(status)="data">
          <b-badge
            pill
            :variant="getStatusColor(data.value)"
          >
            {{ data.value }}
          </b-badge>
        </template>
      </b-table>

      <TablePagination
        v-model="loanHistoryDetailsPagination.currentPage"
        :from="loanHistoryDetailsFrom"
        :to="loanHistoryDetailsTo"
        :total-rows="loanHistoryDetailsPagination.totalEntries"
        :per-page="loanHistoryDetailsPagination.perPage"
      />

      <template
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <FullScreenSpinner :show="showSpinner" />
  </b-card>
</template>

<script>
import {
  BCard, BCardBody, BCardText, BRow, BCol, BTable, BSpinner, BBadge,
  BModal, BDropdown, BDropdownItem, BForm, BTabs, BTab, BFormGroup, BFormCheckbox,
  VBTooltip,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required, maxValue, positive } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions, mapGetters } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import Ripple from 'vue-ripple-directive'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import XLSX from 'xlsx'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import * as FileSaver from 'file-saver'
import TablePagination from '@/views/components/table/TablePagination.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import {
  toYearWithDate, toDateWithYear, toDateWithMonth, tocustomDate,
  toDateWithCustomTime, toDateWithYearFirst, checkDateFormat, dateSpecial,
} from '@/helpers/formatDate'
import getStatusColor from '@/helpers/getStatusColor'
import getEndDate from '@/helpers/getEndDate'
import DatePickerField from '../../../../forms/DatePickerField.vue'
import addSpaceToString from '@/helpers/addSpaceToString'
import {
  toBalance, formatAmount, toFormatAmount, removeMoneyFormat, toKobo, toAmount,
} from '@/helpers/formatAmount'
import TableEntries from '@/views/components/table/TableEntries.vue'
import toNumber from '@/helpers/toNumber'
import { transformBoolean } from '@/helpers/transformers'
import SwitchButton from '../../../../components/switch/Switch.vue'
import Button from '../../../../components/button/Button.vue'
import TextAreaField from '../../../../forms/TextAreaField.vue'
import {
  gender, accessLevel, securityTypes, reportDataDetail, repaymentTypes, interestAccruals,
  repaidFrequencies,
} from '../../../../../helpers/data'
import SelectField from '../../../../forms/SelectField.vue'
import InputField from '../../../../forms/InputField.vue'
import InputNumberField from '../../../../forms/InputNumberField.vue'
import { accountPermissions } from '../../../../../helpers/permissions'
import convertToWords from '@/helpers/amountToWords'

export default {
  components: {
    BCard,
    BCardBody,
    BCardText,
    BRow,
    BCol,
    BBadge,
    BTable,
    BFormGroup,
    BFormCheckbox,
    BDropdown,
    BDropdownItem,
    BModal,
    BSpinner,
    SelectField,
    InputField,
    InputNumberField,
    TextAreaField,
    TablePagination,
    FullScreenSpinner,
    Button,
    SwitchButton,
    TableEntries,
    BTabs,
    BTab,
    ValidationObserver,
    BForm,
    AppCollapse,
    AppCollapseItem,
    DatePickerField,
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      isInitialLoad: true,
      pdfMoneyFormat: toFormatAmount,
      convertToWords,
      checkDateFormat,
      showSpinner: false,
      isReadonly: false,
      isRepaymentEditOkay: false,
      isLoading: false,
      isDisabled: false,
      computedInterest: null,
      isRepayingLoan: false,
      isRestructuringLoan: false,
      isRefinancingLoan: false,
      showComputedInterest: false,
      isLoadingComputeInterest: false,
      isLoadingViewOriginalSchedule: false,
      isLoadingNewViewOriginalSchedule: false,
      isLoadingComputeInterestRefinancing: false,
      isLoadingNewViewOriginalScheduleRefinancing: false,
      accountStatus: 'Active',
      maxValue,
      toBalance,
      toAmount,
      toNumber,
      addSpaceToString,
      positive,
      repaymentSchedulePagination: {
        currentPage: 1,
        totalEntries: 0,
        perPage: 10,
        pageSize: 0,
      },
      required,
      modalTitle: '',
      reportInfo: reportDataDetail,
      interestOptions: [],
      feeOptions: [],
      getStatusColor,
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'customerID', sortable: true, label: 'Customer ID' },
        { key: 'name', sortable: true, label: 'Account Name' },
        { key: 'accountNumber', sortable: true, label: 'Loan Account No' },
        { key: 'branchName', sortable: true, label: 'Branch' },
        { key: 'productName', sortable: true, label: 'Loan Product' },
        { key: 'linkedNumber', sortable: true, label: 'Linked Account Number' },
        { key: 'loanAmount', sortable: true, label: 'Disbursed Amount' },
        { key: 'ledgerBalance', sortable: true, label: 'Principal Bal.' },
        { key: 'outstandingLoanPrincipal', sortable: true, label: 'Principal Outst.' },
        { key: 'unpaidPrincipal', sortable: true, label: 'Unpaid Prin.' },
        { key: 'outstandingLoanInterest', sortable: true, label: 'Unpaid Profit' },
        { key: 'dateCreated', sortable: true, label: 'Date Created' },
        { key: 'disbursedLoan', sortable: true, label: 'Disbursed Loan' },
        { key: 'accountOfficerName', sortable: true, label: 'Account Officer Name' },
        { key: 'employeeID', sortable: true, label: 'IPPIS/Oracle ID' },
        { key: 'collectionBackedBy', sortable: true, label: 'Backed By' },
        { key: 'collectionMandateReference', sortable: true, label: 'Mandate Reference' },
        { key: 'collectionProviderCustomerID', sortable: true, label: 'Provider Customer ID' },
        { key: 'accountStatus', sortable: true, label: 'Account Status' },
        { key: 'actions', label: 'Actions' },
      ],
      repaymentScheduleTableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'paymentDate', label: 'Repayment Date' },
        { key: 'principal', label: 'Principal' },
        { key: 'interest', label: 'Profit' },
        { key: 'fee', label: 'Loan Fee' },
        { key: 'totalRepayment', label: 'Total Repayment' },
        { key: 'paymentStatus', label: 'Payment Status' },
      ],
      paymentScheduleTableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'paymentDate', sortable: true, label: 'Repayment Date' },
        { key: 'selectPrincipal', sortable: true, label: 'Select' },
        { key: 'principal', sortable: true, label: 'Principal' },
        { key: 'selectInterest', sortable: true, label: 'Select' },
        { key: 'interest', sortable: true, label: 'Profit' },
        { key: 'selectFee', sortable: true, label: 'Select' },
        { key: 'fee', sortable: true, label: 'Loan Fee' },
        { key: 'totalRepayment', sortable: true, label: 'Total Repayment' },
        { key: 'paymentStatus', sortable: true, label: 'Payment Status' },
      ],
      pdfRepaymentScheduleTableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'paymentDate', label: 'Repayment Date' },
        { key: 'principal', label: 'Principal' },
        { key: 'interest', label: 'Profit' },
        { key: 'fee', label: 'Loan Fee' },
        { key: 'totalRepayment', label: 'Total Repayment' },
        { key: 'paymentStatus', label: 'Payment Status' },
      ],
      paymentOptions: [{ label: 'Full Repayment', value: 'FullRepayment' }, { label: 'Part Repayment', value: 'PartRepayment' }],
      toDateWithYear,
      toYearWithDate,
      toDateWithYearFirst,
      toDateWithCustomTime,
      dateSpecial,
      maturityDate: '',
      genderOptions: gender,
      buttonLabel: 'Save',
      isFlatAmountRequired: '',
      isPercentageRequired: '',
      isFlatAmount: false,
      isFullPayment: false,
      isPercentage: false,
      datePickerConfig: {
        dateFormat: 'd/m/Y',
        defaultDate: '',
      },
      setMaturityDate(dateStr) {
        this.maturityDate = getEndDate(dateStr, this.lienFormData.tenure)
      },
      filter: {
        accountName: '',
        linkedNumber: '',
        customerID: '',
        referenceNumber: '',
        accountNumber: '',
        productCode: '',
        branchCode: '',
        HasDisbursed: '',
        accountStatus: '',
        sortColumn: '',
        direction: '',
        employeeID: '',
        accountOfficerCode: '',
      },
      accountInfo: {
        accountName: '',
        productName: '',
        accountNumber: '',
        ledgerBalance: '',
        availableBalance: '',
        withdrawalBalance: '',
      },
      interestApplicationOptions: [],
      restructureAmountOptions: [
        { label: 'Original Loan Principal', value: 'LoanPrincipal' },
        { label: 'Current Loan Principal Balance', value: 'PrincipalBalance' },
      ],
      restructureCommenceDateOptions: [
        { label: 'From Inception of the Original Loan', value: 'FromInception' },
        { label: 'From Current Financial Date', value: 'FromToday' },
        { label: 'Choose a Preferred Date', value: 'PrefferedDate' },
      ],
      refinanceCommenceDateOptions: [
        { label: 'From Current Financial Date', value: 'FromToday' },
        { label: 'Choose a Preferred Date', value: 'PrefferedDate' },
      ],
      isPreferedDate: false,
      isProrated: false,

      loanAccountStatusOptions: [
        { label: 'Active', value: 'Active' },
        { label: 'Defaulting', value: 'Defaulting' },
        { label: 'Expired', value: 'Expired' },
        { label: 'Closed', value: 'Closed' },
      ],
      loanAccountStatusFilterOptions: [
        { label: 'Active', value: 'Active' },
        { label: 'Revoked', value: 'Revoked' },
        { label: 'Closed', value: 'Closed' },
      ],
      accessLevelOptions: accessLevel,
      loanAccountList: [],
      endDate: '',
      feeId: '',
      interestId: '',
      securityTypesOptions: securityTypes,
      feeDetails: [],
      loanFeeOptions: [],
      interestDetails: [],
      productOptions: [],
      disbursedLoanOptions: [
        { label: 'Yes', value: 'Yes' },
        { label: 'No', value: 'No' },
      ],
      repaymentSchedules: [],
      repaymentSchedulesEdit: [],
      loanAccountNumber: '',
      repaymentTotal: '',
      currentPrincipal: '',
      currentInterest: '',
      currentFee: '',
      paymentSchedules: [],
      exportableRepaymentSchedules: [],
      principalToProcess: [],
      interestToProcess: [],
      feesToProcess: [],
      schedules: [],
      accountDetails: {
        id: '',
        customerID: '',
        accountName: '',
        accountNumber: '',
        productName: '',
        productCode: '',
        customerAddress: '',
        customerPhoneNumber: '',
        branchName: '',
        disbursedAmount: '',
        tenure: '',
        runningPeriod: '',
        loanStartDate: '',
        accountStatus: '',
        accountOfficer: '',
        penaltyGracePeriod: '',
        loanAmount: '',
        ledgerBalance: '',
        unformattedLedgerBalance: '',
        availableBalance: '',
        principalNotYetDue: '',
        principalBalance: '',
        principalDueAndUnpaid: '',
        interestDueAndUnpaid: '',
        interestNotYetDue: '',
        accruedInterestNotYetDue: '',
        feesNotYetDue: '',
        actualPrincipalToBePaid: '',
        actualInterestToBePaid: '',
        actualFeesToBePaid: '',
        otherChargesToBePaid: '',
        totalAmountToBeRepaid: '',
        paymentOption: '',
        loanFees: [],
        paymentSchedules: [],
        interestCommencementDate: null,
        startDate: null,
        firstRepaymentDate: null,
        interestFreePrincipalAmount: '',
        applyLoanFees: '',
        principalRepaymentType: '',
        principalFrequency: '',
        principalToBeRepaidEvery: 0,
        principalInstallment: 0,
        applicableInterestCode: '',
        applicableInterestRate: 0,
        defaultingInterestRate: 0,
        interestAccrualMode: '',
        interestRepaymentType: '',
        interestFrequency: '',
        interestToBeRepaidEvery: 0,
        interestInstallment: 0,
        restructureAmountOption: '',
        restructureCommencementDateOption: '',
        restructureStartDate: null,
        refinanceAmount: '',
        refinanceCommencementDateOption: '',
        refinanceStartDate: null,

        collectionProviderCustomerID: '',
        collectionAuthorisationCode: '',
        collectionAccountNumber: '',
        collectionBankCode: '',
      },
      mandateHistoryList: {},
      equalMonthlyInstallment: false,
      getInterestInstallment: '',
      previousLoanAccountData: {
        principalToBeRepaidEvery: 0,
        principalFrequency: '',
        interestAccrualMode: '',
        interestRepaymentType: '',
        interestToBeRepaidEvery: 0,
        interestFrequency: '',
        interestInstallment: 0,
      },
      totalRepaymentAmount: '',

      remitaLookUpData: {
        firstName: '',
        lastName: '',
        middleName: '',
        accountNumber: '',
        bvn: '',
        bankCode: '',
        authorisationCode: '',
      },
      salaryPaymentTableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'amount', sortable: true, label: 'Amount' },
        { key: 'paymentDate', sortable: true, label: 'Payment Date' },
        { key: 'bankCode', sortable: true, label: 'Bank Code' },
      ],

      loanHistoryTableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'loanProvider', sortable: true, label: 'Loan Provider' },
        { key: 'loanAmount', sortable: true, label: 'Loan Amount' },
        { key: 'outstandingAmount', sortable: true, label: 'Outstanding Amount' },
        { key: 'repaymentAmount', sortable: true, label: 'Repayment Amount' },
        { key: 'repaymentFreq', sortable: true, label: 'Repayment Frequency' },
        { key: 'loanDisbursementDate', sortable: true, label: 'Loan Disbursement Date' },
        { key: 'status', sortable: true, label: 'Status' },
      ],
      salaryPaymentDetails: [],
      loanHistoryDetails: [],
      loanHistoryDetailsPagination: {
        currentPage: 1,
        totalEntries: 0,
        perPage: 10,
        pageSize: 0,
      },
      salaryPaymentDetailsPagination: {
        currentPage: 1,
        totalEntries: 0,
        perPage: 10,
        pageSize: 0,
      },
      salaryBankOptions: [],
      collectionProviderCustomerID: '',
      collectionAuthorisationCode: '',

      isRemitaSearchLoading: false,
      isRemitaSearchDisabled: false,
      isProceedingWithRemita: false,
      isEditable: false,
      sortBy: 'accountName',
      remitaLoanDisbursementList: {
        responseCode: '',
        responseMsg: '',
      },
      accountOfficerOptions: [],
      setAccountStatus: '',
      corporateInfo: {},
    }
  },
  computed: {
    ...mapGetters({
      corporateInformation: 'operations/getCorporateDetails',
      userDetails: 'auth/getCurrentUser',
      branchOptions: 'operations/branchOptions',
      currentFinancialDate: 'operations/getCurrentFinancialDate',
    }),
    exportableData() {
      return this.exportItems.map(item => ({
        ...item,
      }))
    },
    exportItems() {
      return this.exportableRepaymentSchedules.map((item, index) => ({
        'S/N': index + 1,
        'Repayment Date': tocustomDate(item.paymentDate),
        Principal: item.principal > 0 ? formatAmount(item.principal) : 0,
        Profit: item.interest > 0 ? formatAmount(item.interest) : 0,
        'Loan Fee': item.fee > 0 ? formatAmount(item.fee) : 0,
        'Total Repayment': item.totalRepayment > 0 ? formatAmount(item.totalRepayment) : 0,
        'Payment Status': addSpaceToString(item.paymentStatus),
      }))
    },
    loanHistoryDetailsFrom() {
      return (this.loanHistoryDetailsPagination.currentPage * this.loanHistoryDetailsPagination.perPage) - (this.loanHistoryDetailsPagination.perPage - 1)
    },
    loanHistoryDetailsTo() {
      return Math.min(this.loanHistoryDetailsFrom + this.loanHistoryDetailsPagination.perPage - 1, this.loanHistoryDetailsPagination.totalEntries)
    },
    salaryPaymentDetailsFrom() {
      return (this.salaryPaymentDetailsPagination.currentPage * this.salaryPaymentDetailsPagination.perPage) - (this.salaryPaymentDetailsPagination.perPage - 1)
    },
    salaryPaymentDetailsTo() {
      return Math.min(this.salaryPaymentDetailsFrom + this.salaryPaymentDetailsPagination.perPage - 1, this.salaryPaymentDetailsPagination.totalEntries)
    },
    repaymentFrom() {
      return (this.repaymentSchedulePagination.currentPage * this.repaymentSchedulePagination.perPage) - (this.repaymentSchedulePagination.perPage - 1)
    },
    repaymentTo() {
      return Math.min(this.repaymentFrom + this.repaymentSchedulePagination.perPage - 1, this.repaymentSchedulePagination.totalEntries)
    },
    repaymentLoanAccountNumber() {
      const loanAccountNo = this.exportableRepaymentSchedules.map(item => item.loanAccountNumber)
      return loanAccountNo ? loanAccountNo[0] : ''
    },
    loanAccountHolder() {
      const customerName = this.repaymentSchedules.map(item => item.name)
      return customerName ? customerName[0] : ''
    },
    customerPhoneNumber() {
      const customerPhoneNumber = this.repaymentSchedules.map(item => item.customerPhoneNumber)
      return customerPhoneNumber ? customerPhoneNumber[0] : ''
    },
    customerAddress() {
      const customerAddress = this.repaymentSchedules.map(item => item.customerAddress)
      return customerAddress ? customerAddress[0] : ''
    },
    loanTenure() {
      const loanTenure = this.repaymentSchedules.map(item => item.tenure)
      return loanTenure ? loanTenure[0] : ''
    },
    interestRate() {
      const interestRate = this.repaymentSchedules.map(item => item.applicableInterestRate)
      return interestRate ? interestRate[0] : ''
    },
    loanRepaymentAccount() {
      const loanRepaymentAccount = this.repaymentSchedules.map(item => item.linkedNumber)
      return loanRepaymentAccount ? loanRepaymentAccount[0] : ''
    },
    productName() {
      const productName = this.repaymentSchedules.map(item => item.productName)
      return productName[0]
    },
    totalLoanAmount() {
      return this.exportableRepaymentSchedules.reduce((acc, val) => acc + val.principal, 0)
    },
    totalLoanInterest() {
      return this.exportableRepaymentSchedules.reduce((acc, val) => acc + val.interest, 0)
    },
    totalLoanFees() {
      return this.exportableRepaymentSchedules.reduce((acc, val) => acc + val.fee, 0)
    },
    totalExpectedRepayment() {
      return this.exportableRepaymentSchedules.reduce((acc, val) => acc + val.totalRepayment, 0)
    },
    //
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedRepaymentScheduleTableHeaders() {
      return this.repaymentScheduleTableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedPaymentScheduleTableHeaders() {
      return this.paymentScheduleTableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedPdfRepaymentScheduleTableHeaders() {
      return this.pdfRepaymentScheduleTableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedPaymentOptions() {
      return this.paymentOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    repaymentOptions() {
      return repaymentTypes.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    repaymentFrequencyOptions() {
      return repaidFrequencies.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    interestAccrualOptions() {
      return interestAccruals.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translatedRestructureAmountOptions() {
      return this.restructureAmountOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translatedRestructureCommenceDateOptions() {
      return this.restructureCommenceDateOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translatedRefinanceCommenceDateOptions() {
      return this.refinanceCommenceDateOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translatedLoanAccountStatusFilterOptions() {
      return this.loanAccountStatusFilterOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translatedDisbursedLoanOptions() {
      return this.disbursedLoanOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translatedSalaryPaymentTableHeaders() {
      return this.salaryPaymentTableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedLoanHistoryTableHeaders() {
      return this.loanHistoryTableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      // eslint-disable-next-line no-undef
      this.fetchLoanAccounts('', 'main-filter')
    },
    perPage(newValue) {
      this.perPage = newValue
      // eslint-disable-next-line no-undef
      this.fetchLoanAccounts('', 'main-filter')
    },
    'accountDetails.principalRepaymentType': {
      handler(newValue, oldValue) {
        if (oldValue === undefined || !newValue) return
        this.checkPrincipalRepayment(newValue)
      },
      immediate: false,
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.isInitialLoad = false
    })

    this.fetchLoanAccounts(this.accountStatus)
    this.fetchCASAProducts()
    this.fetchInterestApplicable()
    this.fetchLoanFee()
    this.fetchSalaryBanks()
    this.fetchAccountOfficers()
    this.fetchInstitutionFileUpload()
  },
  methods: {
    ...mapActions({
      getLoanAccounts: 'account/getLoanAccountList',
      updateOverdraft: 'account/updateOverdraftAction',
      getInterests: 'account/getInterestList',
      updateOverdraftStatus: 'account/updateStatus',
      searchCustomerAccount: 'account/searchCustomerAccountAction',
      getCASAProduct: 'account/getProducts',
      getLoanAccount: 'account/getLoanAccount',
      disburseLoan: 'account/disburseLoan',
      getLoanFees: 'account/getLoanFeeList',
      releaseSecurityDeposit: 'account/releaseSecurityDeposit',
      revokeLoan: 'account/revokeLoan',
      repayLoan: 'account/repayLoan',
      restructureLoan: 'account/restructureLoan',
      getComputedInterest: 'account/getComputedInterest',
      getPaymentSchedule: 'account/getPaymentSchedule',
      refinanceLoan: 'account/refinanceLoan',
      getCustomerInfoByCustomerId: 'account/getCustomerInformationByCustomerID',
      fetchMandateHistory: 'account/fetchMandateHistory',
      remitaLoanDisbursement: 'account/remitaLoanDisbursementAction',
      getSalaryBankList: 'account/getSalaryBankList',
      getSalaryHistory: 'account/getSalaryHistory',
      getAccountOfficers: 'account/getAccountOfficerList',
      updateRepaymentSchedules: 'account/updateRepaymentSchedules',
      getInstitutionFileUpload: 'operations/getInstitutionFileUploadList',
      downloadLoanAgreementLetterV1: 'account/downloadLoanAgreementLetterV1',
      downloadLoanAgreementLetterV2: 'account/downloadLoanAgreementLetterV2',
    }),
    onViewRemitaLookup() {
      this.$root.$emit('bv::show::modal', 'remita-lookup-search-table')
    },
    getLoanHistoryPageNumber(index) {
      return (this.loanHistoryDetailsPagination.currentPage * this.loanHistoryDetailsPagination.perPage) - this.loanHistoryDetailsPagination.perPage + index + 1
    },
    salaryPaymentPageNumber(index) {
      return (this.salaryPaymentDetailsPagination.currentPage * this.salaryPaymentDetailsPagination.perPage) - this.salaryPaymentDetailsPagination.perPage + index + 1
    },
    async fetchInstitutionFileUpload() {
      try {
        const response = await this.getInstitutionFileUpload({ permission: accountPermissions.VIEW_LOAN_ACCOUNT_NIB })
        const { status, data } = response
        if (status === 200 && data) {
          this.corporateInfo = {
            stamp: data.stampUrl,
            signature: data.eSignatureUrl,
          }
        }
      } catch (error) {
        this.handleError(error)
      }
    },
    async handleRemitaSearch() {
      this.$refs.remitaLookupRef.validate().then(async success => {
        if (success) {
          try {
            this.isRemitaSearchLoading = true
            this.isRemitaSearchDisabled = true
            const response = await this.getSalaryHistory({
              formData: this.remitaLookUpData,
              permission: accountPermissions.VIEW_LOAN_ACCOUNT_NIB,
            })
            const { status, data } = response
            if (status === 200 && data.data) {
              this.salaryPaymentDetails = data.data.salaryPaymentDetails
              this.loanHistoryDetails = data.data.loanHistoryDetails
              this.salaryPaymentDetailsPagination.totalEntries = data.data.salaryPaymentDetails.length
              this.loanHistoryDetailsPagination.totalEntries = data.data.loanHistoryDetails.length
              this.collectionProviderCustomerID = data.data.customerId
              this.collectionAuthorisationCode = data.data.authorisationCode
              this.isProceedingWithRemita = true
            }
          } catch (error) {
            this.handleError(error)
          } finally {
            this.isRemitaSearchLoading = false
            this.isRemitaSearchDisabled = false
          }
        }
      })
    },
    proceedWithRemita() {
      this.accountDetails.collectionProviderCustomerID = this.collectionProviderCustomerID
      this.accountDetails.collectionAuthorisationCode = this.collectionAuthorisationCode
      this.accountDetails.collectionAccountNumber = this.remitaLookUpData.accountNumber
      this.accountDetails.collectionBankCode = this.remitaLookUpData.bankCode
      this.alertSuccess({ text: this.$t('Remita Integration Acknowledged') })
      this.hideModal('remita-lookup-search-table')
    },
    async fetchAccountOfficers() {
      try {
        const { data, status } = await this.getAccountOfficers({
          search: '',
          page: 0,
          limit: 0,
          permission: accountPermissions.VIEW_LOAN_ACCOUNT_NIB,
        })

        if (status === 200 && data.data) {
          this.accountOfficerOptions = data.data.filter(item => item.status === 'Active').map(item => ({ label: item.name, value: item.staffID }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async fetchSalaryBanks() {
      try {
        const response = await this.getSalaryBankList({
          permission: accountPermissions.VIEW_LOAN_ACCOUNT_NIB,
        })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.salaryBankOptions = data.data.map(item => ({
            label: `${item.name} - ${item.code}`,
            value: item.code.toString(),
          }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    checkPrincipalRepayment(value) {
      this.isProrated = true
      if (value === 'EqualMonthlyInstallment') {
        this.equalMonthlyInstallment = true
        this.previousLoanAccountData = {
          principalToBeRepaidEvery: this.accountDetails.principalToBeRepaidEvery,
          principalFrequency: this.accountDetails.principalFrequency,
          interestAccrualMode: this.accountDetails.interestAccrualMode,
          interestRepaymentType: this.accountDetails.interestRepaymentType,
          interestToBeRepaidEvery: this.accountDetails.interestToBeRepaidEvery,
          interestFrequency: this.accountDetails.interestFrequency,
          interestInstallment: this.accountDetails.interestInstallment,
        }
        this.accountDetails = {
          ...this.accountDetails,
          principalToBeRepaidEvery: 1,
          principalFrequency: 'Monthly',
          interestAccrualMode: 'ReducingBalanceButEqual',
          interestRepaymentType: 'EqualMonthlyInstallment',
          interestToBeRepaidEvery: 1,
          interestFrequency: 'Monthly',
          interestInstallment: this.accountDetails.principalInstallment,
        }
      } else {
        this.equalMonthlyInstallment = false
        this.accountDetails = {
          ...this.accountDetails,
          principalToBeRepaidEvery: this.previousLoanAccountData.principalToBeRepaidEvery,
          principalFrequency: this.previousLoanAccountData.principalFrequency,
          interestAccrualMode: this.previousLoanAccountData.interestAccrualMode,
          interestRepaymentType: this.previousLoanAccountData.interestRepaymentType,
          interestToBeRepaidEvery: this.previousLoanAccountData.interestToBeRepaidEvery,
          interestFrequency: this.previousLoanAccountData.interestFrequency,
          interestInstallment: this.previousLoanAccountData.interestInstallment,
        }
      }
    },
    setPrincipalInstallment(value) {
      if (this.equalMonthlyInstallment) {
        this.accountDetails.interestInstallment = value
      }
    },
    printRepayment() {
      const { repaymentToPrint } = this.$refs
      const printWindow = window.open('', '_blank')
      printWindow.document.write(repaymentToPrint.innerHTML)
      const styleElement = printWindow.document.createElement('style')
      styleElement.textContent = `
        body {
          font-family: 'Arial', sans-serif;
          color: #333;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 20px;
        }

        /* Apply styles to table headers */
        th {
          background-color: #cccccc !important;
          text-transform: uppercase;
          font-size: 14px !important;
        }

        /* Apply styles to table cells */
        td, th {
          padding: 5px;
          text-align: left;
        }

        /* Apply alternating row colors */
        tr:nth-child(even) {
          background-color: #f9f9f9;
        }

        /* Apply hover effect to rows */
        tr:hover {
          background-color: #e5e5e5;
        }
      `
      printWindow.document.head.appendChild(styleElement)
      setTimeout(() => {
        printWindow.document.close()
        printWindow.print()
        printWindow.close()
      }, 1000)
    },
    async onLoanAgreementLetter(items) {
      try {
        this.showSpinner = true
        this.isBusy = true
        const response = await this.downloadLoanAgreementLetterV1({
          accountNumber: items.accountNumber,
          permission: accountPermissions.VIEW_LOAN_ACCOUNT_NIB,
        })
        if (response.status === 200) {
          const fileName = this.$t('Loan Agreement Letter')
          const blob = new Blob([response.data], { type: 'application/pdf' })
          FileSaver.saveAs(blob, `${fileName}.pdf`)
          this.alertSuccess({ text: this.$t('Your request to download Loan Agreement Letter was successful') })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
        this.isBusy = false
      }
    },
    async onLoanAgreementLetterV2(items) {
      try {
        this.showSpinner = true
        this.isBusy = true
        const response = await this.downloadLoanAgreementLetterV2({
          accountNumber: items.accountNumber,
          permission: accountPermissions.VIEW_LOAN_ACCOUNT_NIB,
        })
        if (response.status === 200) {
          const fileName = this.$t('Loan Agreement Letter V2')
          const blob = new Blob([response.data], { type: 'application/pdf' })
          FileSaver.saveAs(blob, `${fileName}.pdf`)
          this.alertSuccess({ text: this.$t('Your request to download Loan Agreement Letter V2 was successful') })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
        this.isBusy = false
      }
    },
    async onViewRemitaMandateHistory(items) {
      try {
        this.showSpinner = true
        const formData = {
          customerId: items.customerID,
          mandateRef: items.collectionMandateReference,
        }
        const response = await this.fetchMandateHistory({
          formData,
          permission: accountPermissions.VIEW_LOAN_ACCOUNT_NIB,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.mandateHistoryList = {
            ...data.data,
            collectionStartDate: data.data.collectionStartDate ? dateSpecial(data.data.collectionStartDate) : '',
            dateOfDisbursement: data.data.dateOfDisbursement ? dateSpecial(data.data.dateOfDisbursement) : '',
          }
          this.$root.$emit('bv::show::modal', 'mandate-history-modal')
        } else if (status === 200 && data.data === null) {
          this.alertError({ text: data.responseMsg })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
      }
    },
    async onRemitaLoanDisbursement(items) {
      try {
        this.showSpinner = true
        const formData = {
          loanAccountNumber: items.accountNumber,
        }
        const response = await this.remitaLoanDisbursement({
          formData,
          permission: accountPermissions.VIEW_LOAN_ACCOUNT_NIB,
        })
        const { status, data } = response
        if (status === 200 && data) {
          this.remitaLoanDisbursementList = {
            responseCode: data.responseCode,
            responseMsg: data.responseMsg,
          }
          this.$root.$emit('bv::show::modal', 'remita-loan-disbursement-modal')
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
      }
    },
    repaymentTableIndex(index) {
      return (this.repaymentSchedulePagination.currentPage * this.repaymentSchedulePagination.perPage) - this.repaymentSchedulePagination.perPage + index + 1
    },
    goToAddLoanAccount() {
      this.$router.push({
        name: 'loan-nib',
        params: {
          type: 'add',
        },
      }).catch(error => {
        this.alertError({ text: error })
      })
    },
    goToEditLoanAccount(item, activity, val) {
      this.$router.push({
        name: 'loan-nib',
        params: {
          type: val,
          id: item.id,
        },
      }).catch(error => {
        this.alertError({ text: error })
      })
    },
    getRepaymentColor(status) {
      const statusColor = {
        FullyPaid: 'text-success',
        PartiallyPaid: 'text-secondary',
        // NotYetDue: 'text-secondary',
        DueAndUnpaid: 'text-danger',
      }
      return statusColor[status]
    },
    setFullRepayment(value) {
      if (value === 'FullRepayment') {
        this.isFullPayment = true
        this.accountDetails.actualPrincipalToBePaid = removeMoneyFormat(this.accountDetails.principalNotYetDue)
        this.accountDetails.actualInterestToBePaid = removeMoneyFormat(this.accountDetails.interestNotYetDue)
        this.setTotalAmountToBeRepaidPrincipal(removeMoneyFormat(this.accountDetails.principalNotYetDue))
        this.feesToProcess = this.schedules.map(item => item.feeID)
        this.principalToProcess = this.schedules.map(item => item.principalID)
        this.interestToProcess = this.schedules.map(item => item.interestID)
      } else {
        this.accountDetails.actualPrincipalToBePaid = ''
        this.accountDetails.actualInterestToBePaid = ''
        this.accountDetails.totalAmountToBeRepaid = ''
        this.isFullPayment = false
        this.feesToProcess = []
        this.principalToProcess = []
        this.interestToProcess = []
      }
    },
    sumPrincipal(val) {
      const selectedPrincipal = this.schedules.filter(item => val.includes(item.principalID)).map(value => value.principal / 100)
      const sumPrincipals = selectedPrincipal.reduce((a, b) => a + b, 0)
      if (sumPrincipals !== 0) {
        this.setTotalAmountToBeRepaidPrincipal(sumPrincipals)
      } else {
        this.setTotalAmountToBeRepaidPrincipal(0)
      }
      this.accountDetails.actualPrincipalToBePaid = sumPrincipals
    },
    sumInterests(val) {
      const selectedInterest = this.schedules.filter(item => val.includes(item.interestID)).map(value => value.interest / 100)
      const sumInterests = selectedInterest.reduce((a, b) => a + b, 0)
      if (sumInterests !== 0) {
        this.setTotalAmountToBeRepaidInterest(sumInterests)
      } else {
        this.setTotalAmountToBeRepaidInterest(0)
      }
      this.accountDetails.actualInterestToBePaid = sumInterests
    },
    sumFees(val) {
      const selectedFee = this.schedules.filter(item => val.includes(item.feeID)).map(value => value.fee / 100)
      const sumFee = selectedFee.reduce((a, b) => a + b, 0)
      if (sumFee !== 0) {
        this.setTotalAmountToBeRepaidFees(sumFee)
      } else {
        this.setTotalAmountToBeRepaidFees(0)
      }

      this.accountDetails.actualFeesToBePaid = sumFee
    },
    async repaymentAction() {
      try {
        this.isRepayingLoan = true
        const paymentSchedulesToProcess = [...this.feesToProcess, ...this.principalToProcess, ...this.interestToProcess].filter(item => item !== null)
        const formData = {
          loanAccountNumber: this.accountDetails.accountNumber,
          accountName: this.accountDetails.accountName,
          tenure: this.accountDetails.tenure,
          productName: this.accountDetails.productName,
          disbursedAmount: this.accountDetails.disbursedAmount,
          branchName: this.accountDetails.branchName,
          paymentOption: this.accountDetails.paymentOption,
          paymentSchedulesToProcess,
          instrumentNumber: '',
          initiatedBy: this.userDetails.email,
          approvedBy: '',
          actualPrincipalToBePaid: toKobo(Number(this.accountDetails.actualPrincipalToBePaid)),
          actualInterestToBePaid: toKobo(Number(this.accountDetails.actualInterestToBePaid)),
          actualFeesToBePaid: toKobo(Number(this.accountDetails.actualFeesToBePaid)),
          actualOtherChargesToBePaid: toKobo(Number(this.accountDetails.otherChargesToBePaid)),
        }
        const { data, status } = await this.repayLoan({ formData, permission: accountPermissions.REPAY_LOAN })

        if (status === 200 && data.isSuccessful) {
          this.hideModal('repayment-modal')
          this.fetchLoanAccounts('Active')
          this.alertSuccess({ text: this.$t('Your request to repay loan was successful') })
          this.resetRepayModal()
        }
        if (status === 200 && !data.isSuccessful) {
          this.alertError({ text: data.message })
          // this.resetRepayModal()
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isRepayingLoan = false
      }
    },
    async handleLoanRepayment(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.repayLoanRules.validate().then(success => {
        if (success) {
          const paymentSchedulesToProcess = [...this.feesToProcess, ...this.principalToProcess, ...this.interestToProcess].filter(item => item !== null)
          if (paymentSchedulesToProcess.length > 0) {
            this.confirm(this.repaymentAction, { text: this.$t('Are you sure you want to repay Loan?') })
          } else {
            this.alertError({ text: this.$t('Please schedules') })
          }
        }
      })
    },
    async onRepayLoan(item) {
      try {
        this.showSpinner = true
        const response = await this.getLoanAccount({ id: item.id, permission: accountPermissions.VIEW_LOAN_ACCOUNT_NIB })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.schedules = data?.data.upcomingSchedules
          this.paymentSchedules = data?.data.upcomingSchedules
            .map(val => ({
              ...val,
              selectPrincipal: val.principalID,
              selectInterest: val.interestID,
              selectFee: val.feeID,
            }))
        }
        this.accountDetails = {
          accountName: data.data.name,
          accountNumber: data.data.accountNumber,
          productName: data.data.productName,
          branchName: data.data.branchName,
          disbursedAmount: toBalance(data.data.loanAmount),
          tenure: data.data.tenure,
          runningPeriod: `${toDateWithYear(data.data.startDate)} - ${toDateWithYear(data.data.maturityDate)} `,
          linkedNumber: data.data.linkedNumber,
          accountStatus: data.data.accountStatus,
          accountOfficer: data.data.accountOfficerName,
          ledgerBalance: toBalance(data.data.ledgerBalance),
          availableBalance: toBalance(data.data.availableBalance),
          principalNotYetDue: toBalance(data.data.principalNotYetDue),
          interestNotYetDue: toBalance(data.data.interestNotYetDue),
          accruedInterestNotYetDue: toBalance(data.data.accruedInterestNotYetDue),
          feesNotYetDue: '',
          actualPrincipalToBePaid: '',
          actualInterestToBePaid: '',
          actualFeesToBePaid: '',
          otherChargesToBePaid: '',
          totalAmountToBeRepaid: '',
          paymentOption: '',
          linkedAccountAccountOfficer: data.data.linkedAccountAccountOfficer,
          linkedAccountAccountProduct: data.data.linkedAccountAccountProduct,
          linkedAccountAccountStatus: data.data.linkedAccountAccountStatus,
          linkedAccountAvailableBalance: toBalance(data.data.linkedAccountAvailableBalance),
          linkedAccountLedgerBalance: toBalance(data.data.linkedAccountLedgerBalance),
        }

        this.showModal('repayment-modal')
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.showSpinner = false
      }
    },

    async onRestructureLoan(item) {
      try {
        this.showSpinner = true
        const response = await this.getLoanAccount({ id: item.id, permission: accountPermissions.VIEW_LOAN_ACCOUNT_NIB })
        const { data, status } = response
        if (status === 200 && data.data) {
          if (data.data.interestRepaymentType === 'Prorated') {
            this.isProrated = true
          }
          this.accountDetails = {
            id: data.data.id,
            accountName: data.data.name,
            accountNumber: data.data.accountNumber,
            productName: data.data.productName,
            customerID: data.data.customerID,
            productCode: data.data.productCode,
            customerAddress: data.data.customerAddress,
            customerPhoneNumber: data.data.customerPhoneNumber,
            disbursedAmount: toBalance(data.data.loanAmount),
            tenure: data.data.tenure,
            runningPeriod: `${toDateWithYear(data.data.startDate)} - ${toDateWithYear(data.data.maturityDate)} `,
            loanStartDate: data.data.startDate,
            principalBalance: toBalance(data.data.ledgerBalance),
            principalDueAndUnpaid: data.data.unpaidPrincipal ? toBalance(data.data.unpaidPrincipal) : '',
            interestDueAndUnpaid: data.data.outstandingLoanInterest ? toBalance(data.data.outstandingLoanInterest) : '',
            penaltyGracePeriod: data.data.penaltyGracePeriod,
            loanAmount: data.data.loanAmount,
            linkedNumber: data.data.linkedNumber,
            loanFees: data.data.loanFees,
            approvedDate: data.data.endDate ? toDateWithYear(data.data.approvedDate) : null,
            interestCommencementDate: data.data.interestCommencementDate ? toDateWithYear(data.data.interestCommencementDate) : null,
            startDate: data.data.startDate ? toDateWithYear(data.data.startDate) : null,
            endDate: data.data.endDate ? toDateWithYear(data.data.endDate) : null,
            interestFreePrincipalAmount: data.data.interestFreePrincipalAmount,
            applyLoanFees: data.data.applyLoanFees,
            paymentSchedules: data.data.paymentSchedules,
            ledgerBalance: toBalance(data.data.ledgerBalance),
            unformattedLedgerBalance: data.data.ledgerBalance,
            availableBalance: toBalance(data.data.availableBalance),
            linkedAccountAccountOfficer: data.data.linkedAccountAccountOfficer,
            linkedAccountAccountProduct: data.data.linkedAccountAccountProduct,
            linkedAccountAccountStatus: data.data.linkedAccountAccountStatus,
            linkedAccountAvailableBalance: toBalance(data.data.linkedAccountAvailableBalance),
            linkedAccountLedgerBalance: toBalance(data.data.linkedAccountLedgerBalance),
            principalRepaymentType: data.data.principalRepaymentType,
            principalFrequency: data.data.principalFrequency,
            principalToBeRepaidEvery: data.data.principalToBeRepaidEvery,
            principalInstallment: data.data.principalInstallment,
            applicableInterestCode: data.data.applicableInterestCode,
            applicableInterestRate: data.data.applicableInterestRate,
            defaultingInterestRate: data.data.defaultingInterestRate,
            interestAccrualMode: data.data.interestAccrualMode,
            interestRepaymentType: data.data.interestRepaymentType,
            interestFrequency: data.data.interestFrequency,
            interestToBeRepaidEvery: data.data.interestToBeRepaidEvery,
            interestInstallment: data.data.interestInstallment,
            restructureAmountOption: data.data.restructureAmountOption,
            restructureCommencementDateOption: data.data.restructureCommencementDateOption,
            restructureStartDate: data.data.restructureStartDate ? toDateWithYear(data.data.restructureStartDate) : null,
            firstRepaymentDate: data.data.firstRepaymentDate ? toDateWithYear(data.data.firstRepaymentDate) : null,
          }
          const [firstName, lastName, middleName] = data.data.name.split(' ')
          this.remitaLookUpData = {
            firstName,
            lastName,
            middleName,
          }
        }
        this.showModal('resturcture-modal')
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.showSpinner = false
      }
    },
    async getApplicableInterestRate(selectedValue) {
      try {
        const response = await this.getInterests({
          search: { code: selectedValue },
          page: 0,
          limit: 0,
          permission: accountPermissions.VIEW_LOAN_ACCOUNT_NIB,
        })
        const { status, data } = await response
        if (status === 200) {
          this.accountDetails.applicableInterestRate = data.data[0].rate
        }
      } catch (error) {
        this.toastAlert({ title: error })
      }
    },
    async onRefinanceLoan(item) {
      try {
        this.showSpinner = true
        const response = await this.getLoanAccount({ id: item.id, permission: accountPermissions.VIEW_LOAN_ACCOUNT_NIB })
        const { data, status } = response
        if (status === 200 && data.data) {
          if (data.data.interestRepaymentType === 'Prorated') {
            this.isProrated = true
          }

          let loanFees
          if (data.data.loanFees) {
            const feeCodes = data.data.loanFees.map(itm => itm.loanFeeCode)
            loanFees = this.feeDetails.filter(items => {
              if (feeCodes.includes(items.code)) {
                return items
              }
              return false
            }).map(itm => ({ label: itm.name, value: itm.code }))
          }
          this.accountDetails = {
            id: data.data.id,
            accountName: data.data.name,
            accountNumber: data.data.accountNumber,
            productName: data.data.productName,
            customerID: data.data.customerID,
            productCode: data.data.productCode,
            customerAddress: data.data.customerAddress,
            customerPhoneNumber: data.data.customerPhoneNumber,
            disbursedAmount: toBalance(data.data.loanAmount),
            tenure: data.data.tenure,
            runningPeriod: `${toDateWithYear(data.data.startDate)} - ${toDateWithYear(data.data.maturityDate)} `,
            loanStartDate: data.data.startDate,
            principalBalance: toBalance(data.data.ledgerBalance),
            principalDueAndUnpaid: data.data.unpaidPrincipal ? toBalance(data.data.unpaidPrincipal) : '',
            interestDueAndUnpaid: data.data.outstandingLoanInterest ? toBalance(data.data.outstandingLoanInterest) : '',
            penaltyGracePeriod: data.data.penaltyGracePeriod,
            loanAmount: data.data.loanAmount,
            linkedNumber: data.data.linkedNumber,
            loanFees,
            approvedDate: data.data.endDate ? toDateWithYear(data.data.approvedDate) : null,
            interestCommencementDate: data.data.interestCommencementDate ? toDateWithYear(data.data.interestCommencementDate) : null,
            startDate: data.data.startDate ? toDateWithYear(data.data.startDate) : null,
            endDate: data.data.endDate ? toDateWithYear(data.data.endDate) : null,
            interestFreePrincipalAmount: data.data.interestFreePrincipalAmount,
            applyLoanFees: data.data.applyLoanFees,
            paymentSchedules: data.data.paymentSchedules,
            unformattedLedgerBalance: data.data.ledgerBalance,
            ledgerBalance: toBalance(data.data.ledgerBalance),
            availableBalance: toBalance(data.data.availableBalance),
            linkedAccountAccountOfficer: data.data.linkedAccountAccountOfficer,
            linkedAccountAccountProduct: data.data.linkedAccountAccountProduct,
            linkedAccountAccountStatus: data.data.linkedAccountAccountStatus,
            linkedAccountAvailableBalance: toBalance(data.data.linkedAccountAvailableBalance),
            linkedAccountLedgerBalance: toBalance(data.data.linkedAccountLedgerBalance),
            principalRepaymentType: data.data.principalRepaymentType,
            principalFrequency: data.data.principalFrequency,
            principalToBeRepaidEvery: data.data.principalToBeRepaidEvery,
            principalInstallment: data.data.principalInstallment,
            applicableInterestCode: data.data.applicableInterestCode,
            applicableInterestRate: data.data.applicableInterestRate,
            defaultingInterestRate: data.data.defaultingInterestRate,
            interestAccrualMode: data.data.interestAccrualMode,
            interestRepaymentType: data.data.interestRepaymentType,
            interestFrequency: data.data.interestFrequency,
            interestToBeRepaidEvery: data.data.interestToBeRepaidEvery,
            interestInstallment: data.data.interestInstallment,
            refinanceAmount: 0,
            refinanceCommencementDateOption: '',
            refinanceStartDate: null,
            firstRepaymentDate: data.data.firstRepaymentDate ? toDateWithYear(data.data.firstRepaymentDate) : null,
          }
          const [firstName, lastName, middleName] = data.data.name.split(' ')
          this.remitaLookUpData = {
            firstName,
            lastName,
            middleName,
          }
        }
        this.showModal('refinance-modal')
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.showSpinner = false
      }
    },
    getCommencementDate(dateOption) {
      if (dateOption === 'FromInception') {
        this.accountDetails.restructureStartDate = this.accountDetails.loanStartDate ? toDateWithYear(this.accountDetails.loanStartDate) : null
        this.isPreferedDate = false
      } else if (dateOption === 'FromToday') {
        this.accountDetails.restructureStartDate = this.currentFinancialDate.financialDate ? toDateWithYear(this.currentFinancialDate.financialDate) : null
        this.isPreferedDate = false
      } else {
        this.accountDetails.restructureStartDate = null
        this.isPreferedDate = true
      }
    },
    getRefinanceCommencementDate(dateOption) {
      if (dateOption === 'FromToday') {
        this.accountDetails.refinanceStartDate = this.currentFinancialDate.financialDate ? toDateWithYear(this.currentFinancialDate.financialDate) : null
        this.isPreferedDate = false
      } else {
        this.accountDetails.refinanceStartDate = null
        this.isPreferedDate = true
      }
    },
    checkInterestRepaymentType(interestType) {
      if (interestType === 'Prorated') {
        this.isProrated = true
      } else {
        this.isProrated = false
      }
    },
    async handleLoanRefinance(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.refinanceLoanRules.validate().then(isSuccess => {
        if (isSuccess) {
          this.confirm(this.refinanceLoanAction, { text: this.$t('Are you sure you want to refinance Loan?') })
        }
      })
    },
    async refinanceLoanAction() {
      try {
        this.isRefinancingLoan = true
        const loanFees = this.accountDetails.loanFees.length
          ? this.accountDetails.loanFees.map(item => {
            if (typeof item !== 'object') {
              return { loanFeeCode: item }
            }
            return { loanFeeCode: item.value }
          }) : []
        const formData = {
          loanAccountNumber: this.accountDetails.accountNumber,
          customerName: this.accountDetails.accountName,
          productCode: this.accountDetails.productCode,
          productName: this.accountDetails.productName,
          refinanceAmount: toKobo(this.accountDetails.refinanceAmount),
          tenure: this.accountDetails.tenure,
          principalRepaymentType: this.accountDetails.principalRepaymentType,
          principalFrequency: this.accountDetails.principalFrequency,
          principalToBeRepaidEvery: this.accountDetails.principalToBeRepaidEvery,
          principalInstallment: this.accountDetails.principalInstallment,
          applicableInterestCode: this.accountDetails.applicableInterestCode,
          applicableInterestRate: this.accountDetails.applicableInterestRate,
          defaultingInterestRate: this.accountDetails.defaultingInterestRate,
          interestAccrualMode: this.accountDetails.interestAccrualMode,
          interestRepaymentType: this.accountDetails.interestRepaymentType,
          interestFrequency: this.accountDetails.interestFrequency,
          interestToBeRepaidEvery: this.accountDetails.interestToBeRepaidEvery,
          interestInstallment: this.accountDetails.interestInstallment,
          refinanceCommencementDateOption: this.accountDetails.refinanceCommencementDateOption,
          refinanceStartDate: toYearWithDate(this.accountDetails.refinanceStartDate),
          applyLoanFees: this.accountDetails.applyLoanFees,
          loanFees,
          collectionProviderCustomerID: this.accountDetails.collectionProviderCustomerID,
          collectionAuthorisationCode: this.accountDetails.collectionAuthorisationCode,
          collectionAccountNumber: this.accountDetails.collectionAccountNumber,
          collectionBankCode: this.accountDetails.collectionBankCode,
          firstRepaymentDate: this.accountDetails.firstRepaymentDate ? toYearWithDate(this.accountDetails.firstRepaymentDate) : null,
        }
        const { data, status } = await this.refinanceLoan({ formData, permission: accountPermissions.REFINANCE_LOAN })
        if (status === 200 && data.isSuccessful) {
          this.hideModal('refinance-modal')
          this.fetchLoanAccounts('Active')
          this.alertSuccess({ text: this.$t('Your request to refinance loan was successful') })
          this.resetRefinanceModal()
        }
        if (status === 200 && !data.isSuccessful) {
          this.alertError({ text: data.message })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isRefinancingLoan = false
      }
    },
    setApplyLoanFee(value) {
      if (!value) {
        this.accountDetails.loanFees = []
        return false
      }
      return true
    },
    async refinanceComputeInterest() {
      this.$refs.refinanceLoanRules.validate().then(isSuccess => {
        if (isSuccess) {
          this.refinanceComputeInterestAction()
        }
      })
    },
    async refinanceComputeInterestAction() {
      try {
        this.showSpinner = true
        this.isLoadingComputeInterestRefinancing = true
        const loanFees = this.accountDetails.loanFees.length
          ? this.accountDetails.loanFees.map(item => {
            if (typeof item !== 'object') {
              return { loanFeeCode: item }
            }
            return { loanFeeCode: item.value }
          }) : []
        const response = await this.getComputedInterest({
          permission: accountPermissions.VIEW_LOAN_ACCOUNT_NIB,
          formData: {
            accountNumber: this.accountDetails.accountNumber,
            name: this.accountDetails.accountName,
            customerID: this.accountDetails.customerID,
            productCode: this.accountDetails.productCode.toString(),
            linkedNumber: this.accountDetails.linkedNumber,
            principalToBeRepaidEvery: this.accountDetails.principalToBeRepaidEvery ? this.accountDetails.principalToBeRepaidEvery : 1,
            principalInstallment: this.accountDetails.principalInstallment,
            interestToBeRepaidEvery: this.accountDetails.interestToBeRepaidEvery ? this.accountDetails.interestToBeRepaidEvery : 1,
            interestInstallment: this.accountDetails.interestInstallment,
            penaltyGracePeriod: this.accountDetails.penaltyGracePeriod,
            loanAmount: this.accountDetails.refinanceAmount ? this.accountDetails.unformattedLedgerBalance + toKobo(this.accountDetails.refinanceAmount) : this.accountDetails.unformattedLedgerBalance,
            interestCommencementDate: this.accountDetails.interestCommencementDate ? toYearWithDate(this.accountDetails.interestCommencementDate) : null,
            startDate: this.accountDetails.refinanceStartDate ? toYearWithDate(this.accountDetails.refinanceStartDate) : null,
            firstRepaymentDate: this.accountDetails.firstRepaymentDate ? toYearWithDate(this.accountDetails.firstRepaymentDate) : null,
            principalRepaymentType: this.accountDetails.principalRepaymentType,
            principalFrequency: this.accountDetails.principalFrequency,
            applicableInterestCode: this.accountDetails.applicableInterestCode,
            applicableInterestRate: this.accountDetails.applicableInterestRate,
            defaultingInterestRate: this.accountDetails.defaultingInterestRate,
            interestRepaymentType: this.accountDetails.principalRepaymentType,
            interestFrequency: this.accountDetails.interestFrequency,
            interestAccrualMode: this.accountDetails.interestAccrualMode,
            interestFreePrincipalAmount: toKobo(this.accountDetails.interestFreePrincipalAmount),
            applyLoanFees: this.accountDetails.applyLoanFees,
            loanFeesRequest: loanFees,
            loanFees: [],
          },
        })
        const totalInterestAmount = `${this.$t('Total Profit Amount')} = ${toBalance(response.data.totalInterestAmount)}`
        const totalDays = `${this.$t('Total Days')} = ${response.data.totalDays}`
        this.computedInterest = `${totalInterestAmount} \n ${totalDays}`

        this.showSpinner = false
        this.isLoadingComputeInterestRefinancing = false
        this.showComputedInterest = true
        // }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
      }
    },
    async onViewRefinance() {
      this.$refs.refinanceLoanRules.validate().then(isSuccess => {
        if (isSuccess) {
          this.onViewRefinanceNewRepaymentSchedules()
        }
      })
    },
    async onViewRefinanceNewRepaymentSchedules() {
      try {
        this.showSpinner = true
        this.isLoadingNewViewOriginalScheduleRefinancing = true
        const loanFees = this.accountDetails.loanFees.length
          ? this.accountDetails.loanFees.map(item => {
            if (typeof item !== 'object') {
              return { loanFeeCode: item }
            }
            return { loanFeeCode: item.value }
          }) : []
        const response = await this.getPaymentSchedule({
          permission: accountPermissions.VIEW_LOAN_ACCOUNT_NIB,
          formData: {
            accountNumber: this.accountDetails.accountNumber,
            name: this.accountDetails.customerName,
            customerID: this.accountDetails.customerID,
            productCode: this.accountDetails.productCode,
            linkedNumber: this.accountDetails.linkedNumber,
            principalToBeRepaidEvery: this.accountDetails.principalToBeRepaidEvery,
            principalInstallment: this.accountDetails.principalInstallment,
            interestToBeRepaidEvery: this.accountDetails.interestToBeRepaidEvery,
            interestInstallment: this.accountDetails.interestInstallment,
            penaltyGracePeriod: this.accountDetails.penaltyGracePeriod,
            loanAmount: this.accountDetails.refinanceAmount ? this.accountDetails.unformattedLedgerBalance + toKobo(this.accountDetails.refinanceAmount) : this.accountDetails.unformattedLedgerBalance,
            interestCommencementDate: this.accountDetails.interestCommencementDate ? toYearWithDate(this.accountDetails.interestCommencementDate) : null,
            startDate: this.accountDetails.refinanceStartDate ? toYearWithDate(this.accountDetails.refinanceStartDate) : null,
            firstRepaymentDate: this.accountDetails.firstRepaymentDate ? toYearWithDate(this.accountDetails.firstRepaymentDate) : null,
            principalRepaymentType: this.accountDetails.principalRepaymentType,
            principalFrequency: this.accountDetails.principalFrequency,
            applicableInterestCode: this.accountDetails.applicableInterestCode,
            useProductInterestRate: this.accountDetails.useProductInterestRate,
            applicableInterestRate: this.accountDetails.applicableInterestRate,
            defaultingInterestRate: this.accountDetails.defaultingInterestRate,
            interestRepaymentType: this.accountDetails.interestRepaymentType,
            interestFrequency: this.accountDetails.interestFrequency,
            interestAccrualMode: this.accountDetails.interestAccrualMode,
            interestFreePrincipalAmount: toKobo(this.accountDetails.interestFreePrincipalAmount),
            applyLoanFees: this.accountDetails.applyLoanFees,
            loanFeesRequest: loanFees,
            loanFees: [],
            allowMoratorium: this.accountDetails.allowMoratorium,
            moratoriumEffect: this.accountDetails.moratoriumEffect,
            moratoriumDuration: this.accountDetails.moratoriumDuration || 0,
            moratoriumFrequency: this.accountDetails.moratoriumFrequency,
          },
        })
        const { status, data } = response
        if (status === 200) {
          this.repaymentSchedules = data.data.map(item => ({
            ...item,
            name: this.accountDetails.accountName,
            customerAddress: this.accountDetails.customerAddress,
            customerPhoneNumber: this.accountDetails.customerPhoneNumber,
            tenure: this.accountDetails.tenure,
            applicableInterestRate: this.accountDetails.applicableInterestRate,
            linkedNumber: this.accountDetails.linkedNumber,
            productName: this.accountDetails.productName,
            fee: toBalance(item.fee),
            interest: toBalance(item.interest),
            paymentDate: toDateWithMonth(item.paymentDate),
            paymentStatus: addSpaceToString(item.paymentStatus),
            principal: toBalance(item.principal),
            totalRepayment: toBalance(item.totalRepayment),
          }))
          this.exportableRepaymentSchedules = data.data
          this.repaymentSchedulePagination.totalEntries = data.recordCount
          this.showSpinner = false
          this.isLoadingNewViewOriginalScheduleRefinancing = false
          this.$root.$emit('bv::show::modal', 'repayment-schedule-modal')
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
      }
    },
    async handleLoanRestructure(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.restructureLoanRules.validate().then(success => {
        if (success) {
          this.confirm(this.restructureLoanAction, { text: this.$t('Are you sure you want to restructure Loan?') })
        }
      })
    },
    async restructureLoanAction() {
      try {
        this.isRestructuringLoan = true
        const formData = {
          loanAccountNumber: this.accountDetails.accountNumber,
          customerName: this.accountDetails.accountName,
          productCode: this.accountDetails.productCode,
          productName: this.accountDetails.productName,
          tenure: this.accountDetails.tenure,
          principalRepaymentType: this.accountDetails.principalRepaymentType,
          principalFrequency: this.accountDetails.principalFrequency,
          principalToBeRepaidEvery: this.accountDetails.principalToBeRepaidEvery,
          principalInstallment: this.accountDetails.principalInstallment,
          applicableInterestCode: this.accountDetails.applicableInterestCode,
          applicableInterestRate: this.accountDetails.applicableInterestRate,
          defaultingInterestRate: this.accountDetails.defaultingInterestRate,
          interestAccrualMode: this.accountDetails.interestAccrualMode,
          interestRepaymentType: this.accountDetails.interestRepaymentType,
          interestFrequency: this.accountDetails.interestFrequency,
          interestToBeRepaidEvery: this.accountDetails.interestToBeRepaidEvery,
          interestInstallment: this.accountDetails.interestInstallment,
          restructureAmountOption: this.accountDetails.restructureAmountOption,
          restructureCommencementDateOption: this.accountDetails.restructureCommencementDateOption,
          restructureStartDate: toYearWithDate(this.accountDetails.restructureStartDate),
          collectionProviderCustomerID: this.accountDetails.collectionProviderCustomerID,
          collectionAuthorisationCode: this.accountDetails.collectionAuthorisationCode,
          collectionAccountNumber: this.accountDetails.collectionAccountNumber,
          collectionBankCode: this.accountDetails.collectionBankCode,
          firstRepaymentDate: this.accountDetails.firstRepaymentDate ? toYearWithDate(this.accountDetails.firstRepaymentDate) : null,
        }
        const { data, status } = await this.restructureLoan({ formData, permission: accountPermissions.RESTRUCTURE_LOAN })
        if (status === 200 && data.isSuccessful) {
          this.hideModal('resturcture-modal')
          this.fetchLoanAccounts('Active')
          this.alertSuccess({ text: this.$t('Your request to restructure loan was successful') })
          this.resetRestructureModal()
        }
        if (status === 200 && !data.isSuccessful) {
          this.alertError({ text: data.message })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isRestructuringLoan = false
      }
    },
    async loanAccountStatus(items) {
      const { id, accountName } = items
      const actionType = items.status === 'Active' ? 'Disable' : 'Active'
      const action = items.status === 'Active' ? 'Disabled' : 'Activate'
      try {
        const updateStatus = async () => {
          this.showSpinner = true
          const response = await this.updateOverdraftStatus({ id, actionType, routeName: 'AccountOverdraft' })
          if (response.status === 200) {
            const msg = items.status === 'Active' ? `${accountName} ${this.$t('deactivated successfully')}` : `${accountName} ${this.$t('activated successfully')}`
            // eslint-disable-next-line no-undef
            this.fetchLoanAccounts('', 'main-filter')
            this.alertSuccess({ text: `${msg}` })
            this.showSpinner = false
          }
        }
        this.confirm(updateStatus, { title: '', text: `${action} ${accountName}?` })
      } catch (error) {
        this.showSpinner = false
        this.handleError(error)
      } finally {
        this.showSpinner = false
      }
    },
    async onReleaseSecurityDeposit(items) {
      const performSecurityDeposit = async () => {
        try {
          this.showSpinner = true
          const formData = {
            loanAccountNumber: items.accountNumber,
            accountName: items.name,
            linkedNumber: items.linkedNumber,
            product: items.productName,
            branch: items.branchName,
          }
          const response = await this.releaseSecurityDeposit({ formData, permission: accountPermissions.RELEASE_SECURITY_DEPOSIT })
          const { status } = response
          if (status === 200) {
            this.showSpinner = false
            // eslint-disable-next-line no-undef
            this.fetchLoanAccounts('', 'main-filter')
            this.alertSuccess({ text: `${this.$t('You have successfully Release Security Deposit of this account')}: ${items.accountNumber}` })
          }
        } catch (error) {
          this.alertError({ text: `${this.$t('Your request to Release Security Deposit failed. Reason')}: ${error}` })
        } finally {
          this.showSpinner = false
        }
      }
      this.confirm(performSecurityDeposit, { title: '', text: `${this.$t('Are you sure you want to Release Security Deposit of this account')}: ${items.accountNumber}?` })
    },
    async fetchInterestApplicable() {
      try {
        const response = await this.getInterests({
          search: { type: 'Debit' },
          page: 0,
          limit: 0,
          permission: accountPermissions.VIEW_LOAN_ACCOUNT_NIB,
        })
        const { status, data } = response
        if (status === 200) {
          this.interestApplicationOptions = data.data.filter(dataItem => dataItem.status === 'Active').map(item => ({
            label: `${this.$t(item.name)} - ${item.rate}%`,
            value: item.code,
          }))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async onDisburse(items) {
      const updateStatus = async () => {
        try {
          this.showSpinner = true
          const payload = {
            id: items.id,
            loanAccountNumber: items.accountNumber,
            accountName: items.name,
            name: items.name,
            branchName: items.branchName,
            productCode: items.productCode,
            productName: items.productName,
            accountOfficerName: items.accountOfficerName,
            accountStatus: items.accountStatus,
            allowDisbursementsToGL: items.allowDisbursementsToGL,
            applicableInterestCode: items.applicableInterestCode,
            applicableInterestRate: items.applicableInterestRate,
            applyLoanFees: items.applyLoanFees,
            approvedDate: items.approvedDate ? toDateWithYearFirst(items.approvedDate) : '',
            availableBalance: items.availableBalance ? formatAmount(items.availableBalance) : 0,
            clearingBankGL: items.clearingBankGL,
            closureDate: items.closureDate,
            collateralDescription: items.collateralDescription,
            collateralType: items.collateralType,
            collateralValuation: items.collateralValuation,
            dateCreated: items.dateCreated ? toDateWithYearFirst(items.dateCreated) : '',
            dateCreatedFinancial: items.dateCreatedFinancial ? toDateWithYearFirst(items.dateCreatedFinancial) : '',
            defaultingInterestRate: items.defaultingInterestRate,
            disbursedLoan: items.disbursedLoan,
            documentIdentifier: items.documentIdentifier,
            documents: items.documents,
            employeeID: items.employeeID,
            firstInstallmentDate: items.firstInstallmentDate ? toDateWithYearFirst(items.firstInstallmentDate) : '',
            firstRepaymentDate: items.firstRepaymentDate ? toDateWithYearFirst(items.firstRepaymentDate) : '',
            guarantor1: items.guarantor1,
            guarantor2: items.guarantor2,
            guarantorID1: items.guarantorID1,
            guarantorID2: items.guarantorID2,
            hasDisbursed: items.hasDisbursed,
            installmentAmount: items.installmentAmount ? formatAmount(items.installmentAmount) : 0,
            interestAccrualMode: items.interestAccrualMode,
            interestCommencementDate: items.interestCommencementDate ? toDateWithYearFirst(items.interestCommencementDate) : '',
            interestFreePrincipalAmount: items.interestFreePrincipalAmount ? formatAmount(items.interestFreePrincipalAmount) : 0,
            interestFrequency: items.interestFrequency,
            interestInstallment: items.interestInstallment,
            interestInstallmentAmount: items.interestInstallmentAmount ? formatAmount(items.interestInstallmentAmount) : 0,
            interestName: items.interestName,
            interestNotYetDue: items.interestNotYetDue,
            interestRepaymentType: items.interestRepaymentType,
            interestToBeRepaidEvery: items.interestToBeRepaidEvery,
            ledgerBalance: items.ledgerBalance ? formatAmount(items.ledgerBalance) : 0,
            linkedAccountAccountOfficer: items.linkedAccountAccountOfficer,
            linkedAccountAccountProduct: items.linkedAccountAccountProduct,
            linkedAccountAccountStatus: items.linkedAccountAccountStatus,
            linkedNumber: items.linkedNumber,
            loanAmount: items.loanAmount ? formatAmount(items.loanAmount) : 0,
            loanFees: items.loanFees,
            maturityDate: items.maturityDate ? toDateWithYearFirst(items.maturityDate) : '',
            paymentSchedules: items.paymentSchedules,
            principalFrequency: items.principalFrequency,
            principalInstallment: items.principalInstallment,
            principalNotYetDue: items.principalNotYetDue,
            principalRepaymentType: items.principalRepaymentType,
            principalToBeRepaidEvery: items.principalToBeRepaidEvery,
            startDate: items.startDate ? toDateWithYearFirst(items.startDate) : '',
            tenure: items.tenure,
            upcomingSchedules: items.upcomingSchedules,
            useProductInterestRate: items.useProductInterestRate,
            initiatedBy: this.userDetails.email,
          }
          const postingApprovalModel = {
            amount: toKobo(payload.loanAmount),
            accountName: payload.accountName,
            initiatedBy: payload.initiatedBy,
            approvedBy: '',
            postingType: 'LoanPosting',
          }
          payload.postingApprovalModel = postingApprovalModel

          const response = await this.disburseLoan(payload)
          const { status, data } = response
          if (status === 200 && data.isSuccessful) {
            this.showSpinner = false
            // eslint-disable-next-line no-undef
            this.fetchLoanAccounts('', 'main-filter')
            this.alertSuccess({ text: `${this.$t('You have successfully disbursed')} ${toBalance(items.loanAmount)} ${this.$t('to')} ${items.name}` })
          }
          if (status === 200 && !data.isSuccessful) {
            this.alertError({ text: `${this.$t('Your Loan disbursement request failed. Reason')}: ${data.message}` })
          }
        } catch (error) {
          this.handleError(error)
        } finally {
          this.showSpinner = false
        }
      }
      this.confirm(updateStatus, { title: '', text: `${this.$t('Are you sure you want to Disburse')} ${toBalance(items.loanAmount)} ${this.$t('to')} ${items.name}?` })
    },
    async handleRevokeLoan(items) {
      const revokeLoan = async () => {
        try {
          this.showSpinner = true
          const formData = {
            loanAccountNumber: items.accountNumber,
            accountName: items.name,
            linkedNumber: items.linkedNumber,
            product: items.productName,
            branch: items.branchName,
          }
          const response = await this.revokeLoan({ formData, permission: accountPermissions.CREATE_LOAN_ACCOUNT_NIB })
          const { status } = response
          if (status === 200) {
            this.showSpinner = false
            // eslint-disable-next-line no-undef
            this.fetchLoanAccounts('', 'main-filter')
            this.alertSuccess({ text: `${this.$t('You have successfully Revoked')} ${items.name}'s ${this.$t('loan application number')} ${items.accountNumber}` })
          }
        } catch (error) {
          this.alertError({ text: `${this.$t('Your request to Revoke Loan failed. Reason')}: ${error}` })
        } finally {
          this.showSpinner = false
        }
      }
      this.confirm(revokeLoan, { title: '', text: `${this.$t('Are you sure you want to Revoke')} ${items.name}'s ${this.$t('loan application number')}: ${items.accountNumber}?` })
    },
    async computeInterest() {
      this.$refs.restructureLoanRules.validate().then(success => {
        if (success) {
          this.computeInterestAction()
        }
      })
    },
    async computeInterestAction() {
      try {
        this.isLoadingComputeInterest = true
        this.showSpinner = true
        const response = await this.getComputedInterest({
          permission: accountPermissions.VIEW_LOAN_ACCOUNT_NIB,
          formData: {
            accountNumber: this.accountDetails.accountNumber,
            name: this.accountDetails.accountName,
            customerID: this.accountDetails.customerID,
            productCode: this.accountDetails.productCode.toString(),
            linkedNumber: this.accountDetails.linkedNumber,
            principalToBeRepaidEvery: this.accountDetails.principalToBeRepaidEvery ? this.accountDetails.principalToBeRepaidEvery : 1,
            principalInstallment: this.accountDetails.principalInstallment,
            interestToBeRepaidEvery: this.accountDetails.interestToBeRepaidEvery ? this.accountDetails.interestToBeRepaidEvery : 1,
            interestInstallment: this.accountDetails.interestInstallment,
            penaltyGracePeriod: this.accountDetails.penaltyGracePeriod,
            loanAmount: this.accountDetails.loanAmount,
            interestCommencementDate: this.accountDetails.interestCommencementDate ? toYearWithDate(this.accountDetails.interestCommencementDate) : null,
            startDate: this.accountDetails.restructureStartDate ? toYearWithDate(this.accountDetails.restructureStartDate) : null,
            firstRepaymentDate: this.accountDetails.firstRepaymentDate ? toYearWithDate(this.accountDetails.firstRepaymentDate) : null,
            principalRepaymentType: this.accountDetails.principalRepaymentType,
            principalFrequency: this.accountDetails.principalFrequency,
            applicableInterestCode: this.accountDetails.applicableInterestCode,
            applicableInterestRate: this.accountDetails.applicableInterestRate,
            defaultingInterestRate: this.accountDetails.defaultingInterestRate,
            interestRepaymentType: this.accountDetails.principalRepaymentType,
            interestFrequency: this.accountDetails.interestFrequency,
            interestAccrualMode: this.accountDetails.interestAccrualMode,
            interestFreePrincipalAmount: toKobo(this.accountDetails.interestFreePrincipalAmount),
            applyLoanFees: this.accountDetails.applyLoanFees,
            loanFeesRequest: null,
            loanFees: [],
          },
        })
        const totalInterestAmount = `${this.$t('Total Profit Amount')} = ${toBalance(response.data.totalInterestAmount)}`
        const totalDays = `${this.$t('Total Days')} = ${response.data.totalDays}`
        this.computedInterest = `${totalInterestAmount} \n ${totalDays}`

        this.showSpinner = false
        this.showComputedInterest = true
        this.isLoadingComputeInterest = false
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
        this.isLoadingComputeInterest = false
      }
    },
    onViewRepaymentSchedules() {
      this.showSpinner = true
      this.isLoadingViewOriginalSchedule = true
      this.repaymentSchedules = this.accountDetails.paymentSchedules && this.accountDetails.paymentSchedules.map(item => ({
        ...item,
        name: this.accountDetails.accountName,
        customerAddress: this.accountDetails.customerAddress,
        customerPhoneNumber: this.accountDetails.customerPhoneNumber,
        tenure: this.accountDetails.tenure,
        applicableInterestRate: this.accountDetails.applicableInterestRate,
        linkedNumber: this.accountDetails.linkedNumber,
        productName: this.accountDetails.productName,
        fee: toBalance(item.fee),
        interest: toBalance(item.interest),
        paymentDate: toDateWithMonth(item.paymentDate),
        paymentStatus: item.paymentStatus,
        principal: toBalance(item.principal),
        totalRepayment: toBalance(item.totalRepayment),
      }))

      this.exportableRepaymentSchedules = this.accountDetails.paymentSchedules
      this.showSpinner = false
      this.isLoadingViewOriginalSchedule = false
      this.$root.$emit('bv::show::modal', 'repayment-schedule-modal')
    },
    getRestructureAmount(selectedItem) {
      if (selectedItem === 'PrincipalBalance') {
        this.accountDetails.loanAmount = this.accountDetails.unformattedLedgerBalance
      }
    },
    async onViewNewRepaymentSchedules() {
      this.$refs.restructureLoanRules.validate().then(success => {
        if (success) {
          this.onViewNewRepaymentSchedulesAction()
        }
      })
    },
    async onViewNewRepaymentSchedulesAction() {
      try {
        this.showSpinner = true
        this.isLoadingNewViewOriginalSchedule = true
        const response = await this.getPaymentSchedule({
          permission: accountPermissions.VIEW_LOAN_ACCOUNT_NIB,
          formData: {
            accountNumber: this.accountDetails.accountNumber,
            name: this.accountDetails.customerName,
            customerID: this.accountDetails.customerID,
            productCode: this.accountDetails.productCode,
            linkedNumber: this.accountDetails.linkedNumber,
            principalToBeRepaidEvery: this.accountDetails.principalToBeRepaidEvery,
            principalInstallment: this.accountDetails.principalInstallment,
            interestToBeRepaidEvery: this.accountDetails.interestToBeRepaidEvery,
            interestInstallment: this.accountDetails.interestInstallment,
            penaltyGracePeriod: this.accountDetails.penaltyGracePeriod,
            loanAmount: this.accountDetails.loanAmount,
            interestCommencementDate: this.accountDetails.interestCommencementDate ? toYearWithDate(this.accountDetails.interestCommencementDate) : null,
            startDate: this.accountDetails.restructureStartDate ? toYearWithDate(this.accountDetails.restructureStartDate) : null,
            firstRepaymentDate: this.accountDetails.firstRepaymentDate ? toYearWithDate(this.accountDetails.firstRepaymentDate) : null,
            principalRepaymentType: this.accountDetails.principalRepaymentType,
            principalFrequency: this.accountDetails.principalFrequency,
            applicableInterestCode: this.accountDetails.applicableInterestCode,
            useProductInterestRate: this.accountDetails.useProductInterestRate,
            applicableInterestRate: this.accountDetails.applicableInterestRate,
            defaultingInterestRate: this.accountDetails.defaultingInterestRate,
            interestRepaymentType: this.accountDetails.interestRepaymentType,
            interestFrequency: this.accountDetails.interestFrequency,
            interestAccrualMode: this.accountDetails.interestAccrualMode,
            interestFreePrincipalAmount: toKobo(this.accountDetails.interestFreePrincipalAmount),
            applyLoanFees: this.accountDetails.applyLoanFees,
            loanFeesRequest: null,
            loanFees: [],
            allowMoratorium: this.accountDetails.allowMoratorium,
            moratoriumEffect: this.accountDetails.moratoriumEffect,
            moratoriumDuration: this.accountDetails.moratoriumDuration || 0,
            moratoriumFrequency: this.accountDetails.moratoriumFrequency,
          },
        })
        const { status, data } = response
        if (status === 200) {
          this.repaymentSchedules = data.data.map(item => ({
            ...item,
            name: this.accountDetails.accountName,
            customerAddress: this.accountDetails.customerAddress,
            customerPhoneNumber: this.accountDetails.customerPhoneNumber,
            tenure: this.accountDetails.tenure,
            applicableInterestRate: this.accountDetails.applicableInterestRate,
            linkedNumber: this.accountDetails.linkedNumber,
            productName: this.accountDetails.productName,
            fee: toBalance(item.fee),
            interest: toBalance(item.interest),
            paymentDate: toDateWithMonth(item.paymentDate),
            paymentStatus: addSpaceToString(item.paymentStatus),
            principal: toBalance(item.principal),
            totalRepayment: toBalance(item.totalRepayment),
          }))
          this.exportableRepaymentSchedules = data.data
          this.repaymentSchedulePagination.totalEntries = data.recordCount
          this.showSpinner = false
          this.isLoadingNewViewOriginalSchedule = false
          this.$root.$emit('bv::show::modal', 'repayment-schedule-modal')
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
      }
    },
    async getRepaymentSchedule(items) {
      try {
        this.showSpinner = true
        const response = await this.getLoanAccount({ id: items.id, permission: accountPermissions.VIEW_LOAN_ACCOUNT_NIB })
        const { status, data } = response

        if (status === 200) {
          this.repaymentSchedules = data.data.paymentSchedules && data.data.paymentSchedules.map(item => ({
            ...item,
            name: items.name,
            customerAddress: items.customerAddress,
            customerPhoneNumber: items.customerPhoneNumber,
            tenure: items.tenure,
            applicableInterestRate: items.applicableInterestRate,
            linkedNumber: items.linkedNumber,
            productName: items.productName,
            fee: toBalance(item.fee),
            interest: toBalance(item.interest),
            paymentDate: toDateWithMonth(item.paymentDate),
            paymentStatus: item.paymentStatus,
            principal: toBalance(item.principal),
            totalRepayment: toBalance(item.totalRepayment),
            originaltotalRepayment: item.totalRepayment,
          }))
          const filteredPaymentSchedule = data.data.paymentSchedules.filter(item => item.paymentStatus === 'NotYetDue')
          this.repaymentSchedulesEdit = filteredPaymentSchedule.map(item => ({
            paymentDate: item.paymentDate ? toDateWithYear(item.paymentDate) : null,
            fee: item.fee ? toBalance(item.fee) : 0,
            interest: item.interest ? toBalance(item.interest) : 0,
            principal: item.principal ? toBalance(item.principal) : 0,
            totalRepayment: item.totalRepayment ? toBalance(item.totalRepayment) : 0,
          }))

          // Calculate the sum total
          const totals = filteredPaymentSchedule.reduce(
            (acc, curr) => {
              acc.fee += formatAmount(curr.fee)
              acc.interest += formatAmount(curr.interest)
              acc.principal += formatAmount(curr.principal)
              return acc
            },
            { fee: 0, interest: 0, principal: 0 }, // Initial accumulator
          )
          const totalSum = totals.fee + totals.interest + totals.principal

          this.repaymentTotal = {
            principal: totals.principal,
            interest: totals.interest,
            fee: totals.fee,
            total: totalSum,
          }
          this.loanAccountNumber = this.repaymentSchedules[0].loanAccountNumber

          this.totalRepaymentAmount = this.repaymentSchedules[0].originaltotalRepayment
          // this.totalRepaymentAmount = this.repaymentSchedules.reduce((acc, curr) => ({ totalRepayment: curr.originaltotalRepayment }), {})
          this.exportableRepaymentSchedules = data.data.paymentSchedules
          this.showSpinner = false
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
      }
    },
    async onViewRepaymentSchedule(items) {
      const { accountStatus } = items
      this.setAccountStatus = accountStatus

      await this.getRepaymentSchedule(items)
      this.$root.$emit('bv::show::modal', 'repayment-schedule-modal')
    },
    editRepaymentSchedule() {
      this.$root.$emit('bv::show::modal', 'edit-repayment-schedule-modal')
    },
    addNewRecord() {
      this.repaymentSchedulesEdit.push({
        paymentDate: null,
        fee: 0,
        interest: 0,
        principal: 0,
        totalRepayment: 0,
      })
    },
    removeRepaymentSchedule(index) {
      this.repaymentSchedulesEdit.splice(index, 1)

      // Update current principal
      this.currentPrincipal = this.repaymentSchedulesEdit.reduce(
        (acc, curr) => {
          acc.principal += curr.principal
          return acc
        },
        { principal: 0 },
      )
    },
    calulatePrincipal(index) {
      const selectedIndex = this.repaymentSchedulesEdit.findIndex((_, idx) => idx === index)
      this.repaymentSchedulesEdit[selectedIndex].totalRepayment = Number(this.repaymentSchedulesEdit[selectedIndex].principal) + Number(this.repaymentSchedulesEdit[selectedIndex].interest) + Number(this.repaymentSchedulesEdit[selectedIndex].fee)
    },
    calulateInterest(index) {
      const selectedIndex = this.repaymentSchedulesEdit.findIndex((_, idx) => idx === index)
      this.repaymentSchedulesEdit[selectedIndex].totalRepayment = Number(this.repaymentSchedulesEdit[selectedIndex].interest) + Number(this.repaymentSchedulesEdit[selectedIndex].principal) + Number(this.repaymentSchedulesEdit[selectedIndex].fee)
    },
    calulateFee(index) {
      const selectedIndex = this.repaymentSchedulesEdit.findIndex((_, idx) => idx === index)
      this.repaymentSchedulesEdit[selectedIndex].totalRepayment = Number(this.repaymentSchedulesEdit[selectedIndex].fee) + Number(this.repaymentSchedulesEdit[selectedIndex].principal) + Number(this.repaymentSchedulesEdit[selectedIndex].interest)
    },
    // calulatePrincipal(index) {
    //   const selectedIndex = this.repaymentSchedulesEdit.findIndex((_, idx) => idx === index)
    //   this.repaymentSchedulesEdit[selectedIndex].totalRepayment = Number(this.repaymentSchedulesEdit[selectedIndex].principal) + Number(this.repaymentSchedulesEdit[selectedIndex].interest) + Number(this.repaymentSchedulesEdit[selectedIndex].fee)

    //   this.currentPrincipal = this.repaymentSchedulesEdit.reduce(
    //     (acc, curr) => {
    //       acc.principal += curr.principal
    //       return acc
    //     },
    //     { principal: 0 },
    //   )

    //   if (this.repaymentTotal.principal < this.currentPrincipal.principal) {
    //     this.isRepaymentEditOkay = true
    //     this.alertError({ text: this.$t('Principal amount entered cannot be greater than Total Principal') })
    //   } else if (this.repaymentTotal.principal > this.currentPrincipal.principal) {
    //     this.isRepaymentEditOkay = true
    //   } else {
    //     this.isRepaymentEditOkay = false
    //   }
    // },
    // calulateInterest(index) {
    //   const selectedIndex = this.repaymentSchedulesEdit.findIndex((_, idx) => idx === index)
    //   this.repaymentSchedulesEdit[selectedIndex].totalRepayment = Number(this.repaymentSchedulesEdit[selectedIndex].interest) + Number(this.repaymentSchedulesEdit[selectedIndex].principal) + Number(this.repaymentSchedulesEdit[selectedIndex].fee)

    //   this.currentInterest = this.repaymentSchedulesEdit.reduce(
    //     (acc, curr) => {
    //       acc.interest += curr.interest
    //       return acc
    //     },
    //     { interest: 0 },
    //   )

    //   if (this.currentInterest.interest > this.repaymentTotal.interest) {
    //     this.isRepaymentEditOkay = true
    //     this.alertError({ text: this.$t('Profit amount entered cannot be greater than Total Profit') })
    //   } else if (this.currentInterest.interest <= this.repaymentTotal.interest) {
    //     this.isRepaymentEditOkay = false
    //   } else {
    //     this.isRepaymentEditOkay = false
    //   }
    // },
    // calulateFee(index) {
    //   const selectedIndex = this.repaymentSchedulesEdit.findIndex((_, idx) => idx === index)
    //   this.repaymentSchedulesEdit[selectedIndex].totalRepayment = Number(this.repaymentSchedulesEdit[selectedIndex].fee) + Number(this.repaymentSchedulesEdit[selectedIndex].principal) + Number(this.repaymentSchedulesEdit[selectedIndex].interest)

    //   this.currentFee = this.repaymentSchedulesEdit.reduce(
    //     (acc, curr) => {
    //       acc.fee += curr.fee
    //       return acc
    //     },
    //     { fee: 0 },
    //   )

    //   if (this.currentFee.fee > this.repaymentTotal.fee) {
    //     this.isRepaymentEditOkay = true
    //     this.alertError({ text: this.$t('Fee amount entered cannot be greater than Total Fee') })
    //   } else if (this.currentFee.fee <= this.repaymentTotal.fee) {
    //     this.isRepaymentEditOkay = false
    //   } else {
    //     this.isRepaymentEditOkay = false
    //   }
    // },
    checkSumDifference() {
      if (this.currentPrincipal.principal < this.repaymentTotal.principal) {
        const difference = this.repaymentTotal.principal - this.currentPrincipal.principal
        this.alertError({ text: `${this.$t('The Principal column requires')} ${toAmount(difference)} ${this.$t('amount to be equal with Total Principal of')} ${toAmount(this.repaymentTotal.principal)}` })
        this.isRepaymentEditOkay = true
      } else if (this.currentPrincipal.principal > this.repaymentTotal.principal) {
        const difference = this.currentPrincipal.principal - this.repaymentTotal.principal
        this.alertError({ text: `${this.$t('The Principal column is greater with')} ${toAmount(difference)} ${this.$t('to be equal with Total Principal of')} ${toAmount(this.repaymentTotal.principal)}` })
        this.isRepaymentEditOkay = true
      } else {
        this.alertSuccess({ text: this.$t('Repayment Schedule Edit seems good, You may continue to Save Changes') })
        this.isRepaymentEditOkay = false
      }

      // else if (this.currentInterest.interest > this.repaymentTotal.interest) {
      //   const difference = this.currentInterest.interest - this.repaymentTotal.interest
      //   this.alertError({ text: `${this.$t('The Profit column is greater with')} ${toAmount(difference)} ${this.$t('to be equal with Total Profit of')} ${toAmount(this.repaymentTotal.interest)}` })
      //   this.isRepaymentEditOkay = true
      // } else if (this.currentFee.fee > this.repaymentTotal.fee) {
      //   const difference = this.currentFee.fee - this.repaymentTotal.fee
      //   this.alertError({ text: `${this.$t('The Fee column is greater with')} ${toAmount(difference)} ${this.$t('to be equal with Total Fee of')} ${toAmount(this.repaymentTotal.fee)}` })
      //   this.isRepaymentEditOkay = true
      // }
    },
    async updateRepaymentSchedule(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.editRepaymentScheduleRules.validate().then(success => {
        if (success) {
          this.confirm(this.repaymentScheduleEditAction, { text: this.$t('Are you sure you want to Save this changes on Repayment Schedule?') })
        }
      })
    },
    async repaymentScheduleEditAction() {
      try {
        this.showSpinner = true
        const repaymentAction = this.repaymentSchedulesEdit.map(item => ({
          paymentDate: item.paymentDate ? toYearWithDate(item.paymentDate) : null,
          principal: item.principal ? toKobo(item.principal) : 0,
          interest: item.interest ? toKobo(item.interest) : 0,
          fee: item.fee ? toKobo(item.fee) : 0,
        }))
        const formData = {
          loanAccountNumber: this.loanAccountNumber,
          initiatedBy: this.userDetails.email,
          schedules: [
            ...repaymentAction,
          ],
        }
        const response = await this.updateRepaymentSchedules({ formData, permission: accountPermissions.MODIFY_PAYMENT_SCHEDULE })
        const { status, data } = await response
        if (status === 200 && data) {
          this.alertSuccess({ text: this.$t('Your request to modify Repayment Schedules was successful?') })
          this.hideModal('edit-repayment-schedule-modal')
          this.hideModal('repayment-schedule-modal')
          this.fetchLoanAccounts('', 'main-filter')
          this.showSpinner = false
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
      }
    },
    async fetchLoanAccounts(accountStatus, type) {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const response = await this.getLoanAccounts({
          search: type === 'main-filter' ? this.filter : { accountStatus },
          page: this.currentPage,
          limit: this.perPage,
          permission: accountPermissions.VIEW_LOAN_ACCOUNT_NIB,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.loanAccountList = data.data.map(item => ({
            ...item,
            disbursedLoan: transformBoolean(item.hasDisbursed),
          }))
          this.totalCount = data.recordCount
          this.isBusy = false
        } else {
          this.loanAccountList = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async fetchLoanFee() {
      try {
        const response = await this.getLoanFees({
          search: '',
          page: 0,
          limit: 0,
          permission: accountPermissions.VIEW_LOAN_ACCOUNT_NIB,
        })
        const { status, data } = await response

        if (status === 200) {
          this.feeDetails = data.data
          this.loanFeeOptions = data.data.reduce((acc, curr) => [...acc, { label: `${curr.name} - ${curr.amount}`, value: curr.code }], [])
        }
      } catch (error) {
        this.toastAlert({ title: `${error}` })
      }
    },
    async fetchCASAProducts() {
      try {
        const response = await this.getCASAProduct({
          search: { type: 'Loan' },
          page: 0,
          limit: 0,
          permission: accountPermissions.VIEW_LOAN_ACCOUNT_NIB,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.productOptions = data.data.map(item => ({ label: item.name, value: item.code }))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    setTotalAmountToBeRepaidPrincipal(value) {
      if (value) {
        this.accountDetails.totalAmountToBeRepaid = Number(this.accountDetails.actualInterestToBePaid || 0) + Number(value) + Number(this.accountDetails.actualFeesToBePaid || 0) + Number(this.accountDetails.otherChargesToBePaid || 0)
      }
    },
    setTotalAmountToBeRepaidInterest(value) {
      if (value) {
        this.accountDetails.totalAmountToBeRepaid = Number(this.accountDetails.actualPrincipalToBePaid || 0) + Number(value) + Number(this.accountDetails.actualFeesToBePaid || 0) + Number(this.accountDetails.otherChargesToBePaid || 0)
      }
    },
    setTotalAmountToBeRepaidFees(value) {
      if (value) {
        this.accountDetails.totalAmountToBeRepaid = Number(this.accountDetails.actualPrincipalToBePaid || 0) + Number(value) + Number(this.accountDetails.otherChargesToBePaid || 0) + Number(this.accountDetails.actualInterestToBePaid || 0)
      }
    },
    setTotalAmountToBeRepaidCharges(value) {
      if (value) {
        this.accountDetails.totalAmountToBeRepaid = Number(this.accountDetails.actualPrincipalToBePaid || 0) + Number(value) + Number(this.accountDetails.actualFeesToBePaid || 0) + Number(this.accountDetails.actualInterestToBePaid || 0)
      }
    },
    excelExport(data) {
      this.isBusy = false
      this.isDisabled = false
      const arrData = data
      const {
        registeredName,
        registeredAddress,
      } = this.corporateInformation
      const filename = `${this.$t('Loan Repayment Schedule')}.xlsx`
      const headerLists = [
        Object.keys(arrData[0]),
      ]
      const Heading = []
      const Data = arrData // All data lists
      const ws = XLSX.utils.aoa_to_sheet(Heading)

      XLSX.utils.sheet_add_aoa(ws, [
        [`${registeredName}`],
        [`${registeredAddress}`],
        [],
        [],
        [`${this.loanAccountHolder}`],
        [this.customerAddress],
        [this.customerPhoneNumber],
      ], { origin: 'A2' })

      XLSX.utils.sheet_add_aoa(ws, [
        [this.$t('Loan Repayment Information')],
        [`${this.$t('Tenure')}: ${this.loanTenure} ${this.$t('Days')}`],
        [`${this.$t('Profit Rate PA')}: ${this.interestRate}%`],
        [`${this.$t('Loan Repayment Account')}: ${this.loanRepaymentAccount}`],
        [`${this.$t('Loan Account Number')}: ${this.repaymentLoanAccountNumber}`],
        [`${this.$t('Product Name')}: ${this.productName}`],
        [`${this.$t('Loan Amount')}: ${toBalance(this.totalLoanAmount)}`],
        [`${this.$t('Loan Profit')}: ${toBalance(this.totalLoanInterest)}`],
        [`${this.$t('Loan Fees')}: ${toBalance(this.totalLoanFees)}`],
        [`${this.$t('Expected Full Repayment')}: ${toBalance(this.totalExpectedRepayment)}`],
      ], { origin: 'G2' })

      XLSX.utils.sheet_add_aoa(ws, headerLists, { origin: 'A14' })

      XLSX.utils.sheet_add_json(ws, Data, {
        skipHeader: true,
        origin: 'A15',
      })

      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, this.$t('Loan Repayment Schedule'))
      XLSX.writeFile(wb, filename)
    },

    pdfExport() {
      const pdfData = this.exportableRepaymentSchedules.map((item, index) => ({
        sn: index + 1,
        paymentDate: tocustomDate(item.paymentDate),
        principal: item.principal > 0 ? toFormatAmount(item.principal) : 0,
        interest: item.interest > 0 ? toFormatAmount(item.interest) : 0,
        fee: item.fee > 0 ? toFormatAmount(item.fee) : 0,
        totalRepayment: item.totalRepayment > 0 ? toFormatAmount(item.totalRepayment) : 0,
        paymentStatus: addSpaceToString(item.paymentStatus),
      }))

      const {
        registeredName,
        registeredAddress,
      } = this.corporateInformation

      const columns = [
        { title: this.$t('S/N'), key: 'sn' },
        { title: this.$t('Repayment date'), key: 'paymentDate' },
        { title: this.$t('Principal'), key: 'principal' },
        { title: this.$t('Interest'), key: 'interest' },
        { title: this.$t('Loan Fee'), key: 'fee' },
        { title: this.$t('Total repayment'), key: 'totalRepayment' },
        { title: this.$t('Payment Status'), key: 'paymentStatus' },
      ]

      const doc = jsPDF('portriat', 'pt', 'a4')
      const institutionLogo = this.userDetails.institutionLogoURL ? this.userDetails.institutionLogoURL : this.reportInfo.orgLogo
      const imgProps = doc.getImageProperties(institutionLogo)
      const pdfWidth = doc.internal.pageSize.getWidth()
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width

      let newWidth
      let newHeight

      const calculateDimensions = (width, height, widthPercentage, heightPercentage) => ({
        newWidth: (width - ((width * widthPercentage) / 100)) / 2,
        newHeight: (height - ((height * heightPercentage) / 100)) / 2,
      })

      if (pdfWidth === pdfHeight) {
        ({ newWidth, newHeight } = calculateDimensions(pdfWidth, pdfHeight, 70, 70))
      } else if (pdfWidth < pdfHeight) {
        ({ newWidth, newHeight } = calculateDimensions(pdfWidth, pdfHeight, 70, 75))
      } else if (pdfWidth > (pdfHeight * 30) / 100) {
        ({ newWidth, newHeight } = calculateDimensions(pdfWidth, pdfHeight, 45, 60))
      }

      doc.addImage(institutionLogo, 'JPEG', 20, 30, newWidth, newHeight)
      doc.setFontSize(14)
      doc.text(20, 160, `${registeredName}`)
      doc.setFontSize(11)
      doc.text(20, 175, `${registeredAddress}`)
      doc.setFontSize(14)
      doc.text(20, 200, `${this.loanAccountHolder}`)
      doc.setFontSize(11)
      doc.text(20, 220, `${this.customerAddress ? this.customerAddress : ''}`)
      doc.setFontSize(11)
      doc.text(20, 235, `${this.customerPhoneNumber ? this.customerPhoneNumber : ''}`)
      doc.setFontSize(16)
      doc.text(575, 40, this.$t('Loan Repayment Information'), { align: 'right' })
      doc.setFontSize(9)
      doc.text(575, 60, `${this.$t('Loan Tenure')}: ${this.loanTenure} ${this.$t('Days')}`, { align: 'right' })
      doc.text(575, 80, `${this.$t('Profit Rate PA')}: ${this.interestRate}%`, { align: 'right' })
      doc.text(575, 100, `${this.$t('Loan Repayment Account')}: ${this.loanRepaymentAccount}`, { align: 'right' })
      doc.text(575, 120, `${this.$t('Loan Account Number')}: ${this.repaymentLoanAccountNumber}`, { align: 'right' })
      doc.text(575, 140, `${this.$t('Product Name')}: ${this.productName}`, { align: 'right' })
      doc.text(575, 160, `${this.$t('Loan Amount')}: NGN ${toFormatAmount(this.totalLoanAmount)}`, { align: 'right' })
      doc.text(575, 180, `${this.$t('Loan Profit')}: NGN ${toFormatAmount(this.totalLoanInterest)}`, { align: 'right' })
      doc.text(575, 200, `${this.$t('Loan Fees')}: NGN ${toFormatAmount(this.totalLoanFees)}`, { align: 'right' })
      doc.text(575, 220, `${this.$t('Expected Full Repayment')}: NGN ${toFormatAmount(this.totalExpectedRepayment)}`, { align: 'right' })

      doc.autoTable({
        theme: 'grid', // grid, plain, striped
        headStyles: {
          fillColor: [189, 189, 189],
        },
        columns,
        body: pdfData,
        startY: 240,
        // split overflowing columns into pages
        horizontalPageBreak: true,
        // repeat this column in split pages
        horizontalPageBreakRepeat: 'id',
        tableWidth: 'auto', // 'auto', 'wrap' or a number,
        margin: {
          horizontal: 20,
          vertical: 20,
        },
        bodyStyles: {
          valign: 'top',
        },
        styles: {
          overflow: 'linebreak',
          cellWidth: 'wrap',
          maxCellHeight: 4,
          fontSize: 10,
          cellPadding: 10,
        },
        columnStyles: {
          text: {
            cellWidth: 'wrap',
          },
        },
      })
      // Footer page number
      const pages = doc.internal.getNumberOfPages()
      const pageWidth = doc.internal.pageSize.width
      const pageHeight = doc.internal.pageSize.height
      doc.setFontSize(5)

      for (let j = 1; j < pages + 1; j += 1) {
        const horizontalPos = pageWidth / 2 // Can be fixed number
        const verticalPos = pageHeight - 10 // Can be fixed number
        doc.setPage(j)
        doc.text(`${this.$t('page')} ${j} ${this.$t('of')} ${pages}`, horizontalPos, verticalPos, { align: 'center' }) // Optional text styling});
      }
      doc.save(`${this.$t('Loan Repayment Schedule')}.pdf`)
    },

    validateRepayment() {
      return new Promise((resolve, reject) => {
        this.$refs.repayLoanRules.validate().then(success => {
          if (success) {
            resolve(true)
          } else {
            reject()
          }
        })
      })
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      // eslint-disable-next-line no-undef
      this.fetchLoanAccounts('', 'main-filter')
    },
    resetRepayModal() {
      Object.keys(this.accountDetails).forEach(key => {
        this.accountDetails[key] = ''
      })
      this.feesToProcess = []
      this.principalToProcess = []
      this.interestToProcess = []
      this.isFullPayment = false
    },
    resetRestructureModal() {
      Object.keys(this.accountDetails).forEach(key => {
        this.accountDetails[key] = ''
      })
    },
    resetRefinanceModal() {
      Object.keys(this.accountDetails).forEach(key => {
        this.accountDetails[key] = ''
      })
    },
  },
}
</script>

<style lang="scss" scoped>
#toPrinter {
  display: none;
}
.upload-image{
    border: 2px dashed #DCD9D9;
    position: relative;
    width: 250px;
    height: 250px
}
.format {
    color: #999999 !important;
}
form input[type=file]{
  position: absolute;
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  outline: none;
  opacity: 0;
}
.upload-instruction{
  padding-top: 22%;
  color: #000;
  text-align: center;
}
.trash {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
  background: #fff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
  margin-right: 10px;
  cursor: pointer;
}
.view-details {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  background: #fff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
  margin-left: 10px;
  cursor: pointer;
}
</style>
<style scoped lang="scss">
@import '@core/scss/vue/libs/vue-wizard.scss';
@import '@core/scss/vue/libs/vue-select.scss';
@media print {
  html, body {
     height: 99%;
     page-break-after: avoid !important;
     page-break-before: avoid !important;
     visibility:hidden;
  }
  #toPrinter {
    display: block;
  }
}
.print-color {
  color: #000000;
}
img {
  max-width: 50%;
  height: auto;
}
</style>
